/* eslint-disable max-lines */
// eslint-disable-next-line import/no-unresolved
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  cancelPrxShipmentApi,
  generateLabelForShipmentApi,
  getRecipientAddressesApi,
  getShipmentLabelApi,
} from 'api/order';
import { recordAuditEventApi } from 'api/patient';
import axios from 'axios';
import { AuditEventStatus, AuditEventType } from 'common/constants/auditEvents';
import DateField from 'common/forms/DateField';
import {
  validateDateNotInPast,
  validateGreaterThan,
  validateRequired,
  validateTimeNotInFuture,
} from 'common/forms/formValidations';
import SelectField from 'common/forms/SelectField';
import FormTextField from 'common/forms/TextField';
import TimeField from 'common/forms/TimeField';
import perfectRxTheme from 'common/theme/perfectRxTheme';
import { casingFormat, convertZplBlobToPdfUrl, hex2rgba } from 'common/utils';
import { getPickupVoidRestriction } from 'common/utils/shipmentVoidRestriction';
import { addDays, endOfDay, format, isValid, parseISO, startOfDay, subDays } from 'date-fns';
import { ShipmentStatus, shipmentMode } from 'enums/common.enum';
import { RiShip2Fill } from 'react-icons/ri';
import { v4 as uuidV4, v4 as uuidv4 } from 'uuid';
import { recordShippingAuditEventThunk } from 'store/thunks/eventThunks';

import { ClientPrintJob, DefaultPrinter, JSPrintManager, WSStatus } from 'jsprintmanager';
import { find, isEmpty, map, sortBy } from 'lodash';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Form, useFormState } from 'react-final-form';
import { useHotkeys } from 'react-hotkeys-hook';
import { Document, Page } from 'react-pdf';
import { useDispatch, useSelector } from 'react-redux';
import { listShippingOptions } from 'store/thunks/batchShipmentThunk';
import { handleError } from 'store/thunks/errorHandlerThunks';
import { createOrderShipment } from 'store/thunks/patientThunks';
import { workQueueActions } from 'store/slices/workQueueSlice';
import {
  debouncedUpdateShipment,
  deletePrxShipment,
  updateShipment,
} from 'store/thunks/workQueueThunks';
import ShippingTimelineModal from 'common/modules/ShippingTimelineModal';
import RichTextViewer from 'common/components/RichTextViewer';
import CancelShipmentDialog from './CancelShipmentDialog';
import ShipmentRoomUsers from './ShipmentRoomUsers';
import EditShipmentAddressDialog from './EditShipmentAddressDialog';
import SelectPatientAddressDialog from './SelectPatientAddressDialog';
import ManageShipmentItemsDialog from './ManageShipmentItemsDialog';
import RemoveShipmentItemsDialog from './RemoveShipmentItemsDialog';

const parseLocalDate = (str) => {
  if (typeof str !== 'string') return null;

  const date = parseISO(str, 'yyyy-MM-dd', new Date());

  return isValid(date) ? date : null;
};

export const formatShipDate = (shipDate) => {
  if (!shipDate) return '—';

  // Case 1: Date-only (yyyy-MM-dd)
  if (/^\d{4}-\d{2}-\d{2}$/.test(shipDate)) {
    const parsed = parseISO(shipDate); // no timezone shift
    return format(parsed, 'MM/dd/yyyy');
  }

  // Case 2: Full ISO timestamp (with time / Z)
  const parsed = new Date(shipDate); // converts from UTC properly
  return format(parsed, 'MM/dd/yyyy');
};

/** Map technical validation reasons to user-friendly display text. */
const DEFICIENCY_DISPLAY_MAP = {
  'No resolved addresses returned':
    'Address could not be verified — please check the street address',
  'Missing streetLinesToken in resolved address': 'Street address could not be confirmed',
  'No attributes in resolved address':
    'Address verification was incomplete — please verify manually',
  'Missing or invalid secondary address information (apt/suite)':
    'Apartment, suite, or unit number may be missing or incorrect',
};

const formatDeficiency = (raw) => {
  if (DEFICIENCY_DISPLAY_MAP[raw]) return DEFICIENCY_DISPLAY_MAP[raw];
  if (raw.startsWith('Ambiguous:'))
    return 'Multiple possible addresses found — please add more detail (apt, suite, etc.)';
  if (raw.includes('Resolved attribute is'))
    return 'Address could not be confirmed as a valid delivery location';
  if (raw.includes('DPV attribute is')) return 'This may not be a deliverable address';
  if (raw.includes('Address is interpolated'))
    return 'Exact address not found — location is estimated between known addresses';
  return raw;
};

// Static config mapping service code (API format) -> allowed packages (API format)
const dimensionOptions = [
  { label: '8 x 8 x 8 in', length: 8, width: 8, height: 8 },
  { label: '10 x 10 x 10 in', length: 10, width: 10, height: 10 },
  { label: '12 x 12 x 12 in', length: 12, width: 12, height: 12 },
  { label: '14 x 14 x 14 in', length: 14, width: 14, height: 14 },
];

// POM-5507: message shown inline (above Create Label) for update/create-label
// failures, instead of the global blocking dialog. Precedence mirrors handleError.
const deriveShipmentErrorMessage = (error) => {
  const errorCode = error?.response?.errors?.[0]?.extensions?.code;

  if (errorCode === 'ADDRESS_EXCEEDS_FEDEX_LABEL_LIMITS') {
    return 'This address is invalid. Please change the address before generating a shipping label.';
  }

  return (
    error?.response?.errors?.[0]?.message ||
    error?.response?.message ||
    error?.message ||
    'Something went wrong. Please try again.'
  );
};

const servicePackageConfig = {
  FEDEX_STANDARD_OVERNIGHT: {
    serviceCode: 'FEDEX_STANDARD_OVERNIGHT',
    packages: ['FEDEX_PAK', 'YOUR_PACKAGING'],
  },
  FEDEX_2DAY: {
    serviceCode: 'FEDEX_2DAY',
    packages: ['FEDEX_ONE_RATE', 'YOUR_PACKAGING'],
  },
  FEDEX_PRIORITY_OVERNIGHT: {
    serviceCode: 'FEDEX_PRIORITY_OVERNIGHT',
    packages: ['FEDEX_PAK', 'YOUR_PACKAGING'],
  },
};

// Helper to normalize / display service & package codes
const shippingDisplayOverrides = {
  YOUR_PACKAGING: 'Custom Box',
  FEDEX_PAK: 'FedEx\u00AE Pak',
  FEDEX_ONE_RATE: 'FedEx One Rate\u00AE',
  FEDEX_2DAY: 'FedEx 2Day\u00AE',
  FEDEX_STANDARD_OVERNIGHT: 'FedEx Standard Overnight\u00AE',
  FEDEX_PRIORITY_OVERNIGHT: 'FedEx Priority Overnight\u00AE',
};

const getShippingOption = (option, forDisplay = false) => {
  if (!option) return '';
  const normalized = option.toLowerCase();

  if (!forDisplay) return normalized;

  if (shippingDisplayOverrides[option]) return shippingDisplayOverrides[option];

  return normalized.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const Transition = forwardRef((props, ref) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Slide direction='up' ref={ref} {...props} timeout={300} />
));

// Syncs Form shipDate to parent state only when user changes the date (avoids spurious API call on open)
const ShipDateFormSyncer = ({ setShipDate }) => {
  const { values, dirtyFields } = useFormState({
    subscription: { values: true, dirtyFields: true },
  });
  useEffect(() => {
    if (!dirtyFields?.shipDate) return;
    const d = values?.shipDate;
    if (d && isValid(d)) {
      setShipDate(format(new Date(d), 'yyyy-MM-dd'));
    }
  }, [values?.shipDate, dirtyFields?.shipDate, setShipDate]);
  return null;
};

ShipDateFormSyncer.propTypes = {
  setShipDate: PropTypes.func.isRequired,
};

// Trigger validation on mount so invalid initial value (e.g. past date from API) shows error in DateField
const TriggerShipDateValidation = ({ form }) => {
  useEffect(() => {
    if (form?.blur) form.blur('shipDate');
  }, [form]);
  return null;
};

TriggerShipDateValidation.propTypes = {
  form: PropTypes.shape({ blur: PropTypes.func }),
};

TriggerShipDateValidation.defaultProps = {
  form: undefined,
};

const ShipmentDetailHotkeys = ({ handleCreateLabel, handlePrintLabel, handleChangeService }) => {
  useHotkeys('alt+shift+s', () => {
    if (handlePrintLabel) {
      handlePrintLabel();
    } else {
      handleCreateLabel?.();
    }
  });

  useHotkeys('alt+shift+f', () => {
    handleChangeService('FEDEX_PRIORITY_OVERNIGHT');
  });

  useHotkeys('alt+shift+d', () => {
    handleChangeService('FEDEX_2DAY');
  });

  return null;
};

ShipmentDetailHotkeys.propTypes = {
  handleCreateLabel: PropTypes.func,
  handlePrintLabel: PropTypes.func,
  handleChangeService: PropTypes.func.isRequired,
};

ShipmentDetailHotkeys.defaultProps = {
  handleCreateLabel: null,
  handlePrintLabel: null,
};

const ShipmentViewDialog = ({
  open,
  onClose,
  shipment,
  onAfterMutation,
  disableVoidLabel,
  hideOrderCancelOption,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const userAuth = useSelector(({ auth }) => auth);
  const [numPages, setNumPages] = useState(null);
  const fulfillingPharmacies = userAuth?.fulfillingPharmacies;
  const currentTenantId = userAuth?.currentTenantId;
  const facilities = useSelector(({ pharmacy }) => pharmacy[currentTenantId] || []);
  const [displayShipment, setDisplayShipment] = useState(shipment);
  const [shipFrom, setShipFrom] = useState('');
  const [weightLbs, setWeightLbs] = useState('');
  const [weightOz, setWeightOz] = useState('');
  const [service, setService] = useState('');
  const [packageCode, setPackageCode] = useState('');
  const [dimensions, setDimensions] = useState('');
  const [hasInsurance, setHasInsurance] = useState(false);
  const [insuranceAmount, setInsuranceAmount] = useState('');
  const [shipDate, setShipDate] = useState('');
  const [enableSaturdayDelivery, setEnableSaturdayDelivery] = useState(false);
  const [signatureRequired, setSignatureRequired] = useState(false);
  const [shippingService, setShippingService] = useState('FedEx');
  const [isConfigExpanded, setIsConfigExpanded] = useState(true);
  const [isShipmentItemsExpanded, setIsShipmentItemsExpanded] = useState(true);
  const [manageItemsOpen, setManageItemsOpen] = useState(false);
  const [removeItemsOpen, setRemoveItemsOpen] = useState(false);
  const [isManualFormExpanded, setIsManualFormExpanded] = useState(true);
  const [isSendingToShipStation, setIsSendingToShipStation] = useState(false);
  const [isSendToShipstationDialogOpen, setIsSendToShipstationDialogOpen] = useState(false);
  const [shipStationShipmentType, setShipStationShipmentType] = useState('');
  // isCreatingLabel and labelProgress are tracked in Redux (keyed by shipmentId) so they
  // survive dialog close/reopen and don't bleed across different open shipments.
  const labelGenerationState = useSelector(
    ({ workQueue }) => workQueue.labelGeneration?.[shipment?.shipmentId]
  );
  const isCreatingLabel = labelGenerationState?.isCreating ?? false;
  const labelProgress = labelGenerationState?.progress ?? '';
  const [shipmentLabelStatus, setShipmentLabelStatus] = useState('');
  const [updatedTrackingNumber, setUpdatedTrackingNumber] = useState(null);
  const [updatedShippedAt, setUpdatedShippedAt] = useState(null);
  const [updatedLabelUrl, setUpdatedLabelUrl] = useState(null);
  const [isUpdatingShipment, setIsUpdatingShipment] = useState(false);
  const [allItemsMoved, setAllItemsMoved] = useState(false);
  const [inlineLabelError, setInlineLabelError] = useState('');

  // POM-5507: auto-dismiss the inline error after a few seconds. It also clears
  // on the close (✕), on any config change, and on the next Create Label attempt.
  useEffect(() => {
    if (!inlineLabelError) return undefined;
    const timer = setTimeout(() => setInlineLabelError(''), 6000);
    return () => clearTimeout(timer);
  }, [inlineLabelError]);
  const [updatedCost, setUpdatedCost] = useState(null);
  const isInitialLoadRef = useRef(true);
  const initialValuesRef = useRef({
    service: '',
    packageCode: '',
    dimensions: '',
    weightLbs: '',
    weightOz: '',
    hasInsurance: false,
    insuranceAmount: '',
    shipDate: '',
    enableSaturdayDelivery: false,
    signatureRequired: false,
  });
  const [zplBlob, setZplBlob] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [isEditAddressDialogOpen, setIsEditAddressDialogOpen] = useState(false);
  const [isSelectAddressOpen, setIsSelectAddressOpen] = useState(false);
  const [addressMenuAnchor, setAddressMenuAnchor] = useState(null);
  const [recipientAddresses, setRecipientAddresses] = useState([]);
  const [isLoadingRecipientAddresses, setIsLoadingRecipientAddresses] = useState(false);
  const [recipientAddressesError, setRecipientAddressesError] = useState('');
  const [isSavingRecipientAddress, setIsSavingRecipientAddress] = useState(false);
  const hasLoadedRecipientAddressesRef = useRef(false);
  const [isValidatingAddress, setIsValidatingAddress] = useState(false);
  const [selectedAddressType, setSelectedAddressType] = useState('input');
  const [labelMenuAnchor, setLabelMenuAnchor] = useState(null);
  const [voidLabelDialogOpen, setVoidLabelDialogOpen] = useState(false);
  const [isVoidingLabel, setIsVoidingLabel] = useState(false);
  const [isDeficiencyExpanded, setIsDeficiencyExpanded] = useState(false);
  const [cancelShipmentDialogOpen, setCancelShipmentDialogOpen] = useState(false);
  const [isCancellingShipment, setIsCancellingShipment] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const dispatch = useDispatch();
  const shippingOptions = useSelector(({ systemConfigs }) => systemConfigs.shippingOptions || []);

  useEffect(() => {
    if (isEmpty(shippingOptions)) {
      dispatch(listShippingOptions());
    }
  }, [dispatch, shippingOptions]);

  const activeShipmentIdRef = useRef(null);
  const updateSeqRef = useRef(0);
  const lastOpenedShipmentIdRef = useRef(null);
  // True once the shipment address is persisted to the backend via the recipient drawer /
  // edit dialog. The pre-label re-sync is then skipped so it can't re-send a stale address.
  const addressPersistedRef = useRef(false);

  useEffect(() => {
    debouncedUpdateShipment.cancel();
    activeShipmentIdRef.current = shipment?.shipmentId ?? null;
    updateSeqRef.current = 0;
    setIsUpdatingShipment(false);

    if (!shipment) {
      setDisplayShipment(null);
      setIsValidatingAddress(false);
      setIsDeficiencyExpanded(false);
      setShipmentLabelStatus('');
      setUpdatedTrackingNumber(null);
      setUpdatedShippedAt(null);
      setUpdatedLabelUrl(null);
      setUpdatedCost(null);
      setZplBlob(null);
      setPdfBlobUrl(null);
      setSelectedAddressType('input');
      setAllItemsMoved(false);
      setIsSelectAddressOpen(false);
      setRecipientAddresses([]);
      setRecipientAddressesError('');
      setIsLoadingRecipientAddresses(false);
      setIsSavingRecipientAddress(false);
      hasLoadedRecipientAddressesRef.current = false;
      return;
    }

    setDisplayShipment(shipment);
    setAllItemsMoved(false);
    setIsSelectAddressOpen(false);
    setRecipientAddresses([]);
    setRecipientAddressesError('');
    hasLoadedRecipientAddressesRef.current = false;
    setIsValidatingAddress(false);
    setIsDeficiencyExpanded(false);

    // Detect if the current address matches the validated address (user previously selected it)
    const va = shipment.validatedShipmentAddress;
    const sa = shipment.shipmentAddress;
    const isCurrentValidated =
      va &&
      sa &&
      va.line1 === sa.line1 &&
      va.city === sa.city &&
      va.state === sa.state &&
      va.zip === sa.zip;
    setSelectedAddressType(isCurrentValidated ? 'validated' : 'input');

    setShippingService('FedEx');
    setShipStationShipmentType('');

    setShipmentLabelStatus('');
    setUpdatedTrackingNumber(null);
    setUpdatedShippedAt(null);
    setUpdatedLabelUrl(null);
    setUpdatedCost(null);
    setZplBlob(null);
    setPdfBlobUrl(null);

    if (shipment?.fulfillingPharmacyId) {
      setShipFrom(shipment.fulfillingPharmacyId);
    } else {
      setShipFrom('');
    }

    let initialService = '';
    let initialPackageCode = '';
    let initialDimensions = '';
    let initialWeightLbs = '';
    let initialWeightOz = '';

    if (shipment?.serviceCode) {
      initialService = shipment.serviceCode;
      setService(shipment.serviceCode);
      const config = servicePackageConfig[shipment.serviceCode];
      if (config) {
        const defaultPackage = config.packages[0];
        initialPackageCode = shipment.packageCode || defaultPackage || '';
      } else {
        initialPackageCode = shipment.packageCode || '';
      }
      setPackageCode(initialPackageCode);

      // Restore dimensions from shipment if package is YOUR_PACKAGING
      if (initialPackageCode === 'YOUR_PACKAGING' && shipment.dimensions) {
        const { length, width, height } = shipment.dimensions;
        const key = `${length}x${width}x${height}`;
        if (dimensionOptions.some((d) => `${d.length}x${d.width}x${d.height}` === key)) {
          initialDimensions = key;
        }
      }
      setDimensions(initialDimensions);
    } else {
      setService('');
      setPackageCode('');
      setDimensions('');
    }

    if (shipment?.shipmentWeight) {
      const { value, units } = shipment.shipmentWeight;
      const weightValue = parseFloat(value) || 0;
      const weightUnits = units?.toLowerCase() || '';

      let totalWeightOz = 0;

      if (['lbs', 'lb', 'pound', 'pounds'].includes(weightUnits)) {
        totalWeightOz = weightValue * 16;
      }
      if (['oz', 'ounce', 'ounces'].includes(weightUnits)) {
        totalWeightOz = weightValue;
      }
      if (['kg', 'kilogram', 'kilograms'].includes(weightUnits)) {
        totalWeightOz = weightValue * 35.274;
      }
      if (['g', 'gram', 'grams'].includes(weightUnits)) {
        totalWeightOz = weightValue * 0.035274;
      }

      const totalLbs = Math.floor(totalWeightOz / 16);
      const remainingOz = totalWeightOz % 16;
      // Preserve fractional oz (e.g. 0.01875 lb → 0.3 oz) instead of rounding to 0
      const ozDisplay =
        remainingOz > 0 ? parseFloat(Number(remainingOz).toFixed(2)).toString() : '';

      initialWeightLbs = totalLbs > 0 ? totalLbs.toString() : '';
      initialWeightOz = ozDisplay;

      setWeightLbs(initialWeightLbs);
      setWeightOz(initialWeightOz);
    } else {
      setWeightLbs('');
      setWeightOz('');
    }

    // Initialize insurance from backend value
    const { insuranceAmountInUSD } = shipment;
    const initialInsuranceAmount =
      typeof insuranceAmountInUSD === 'number' ? insuranceAmountInUSD : 0;

    const initialHasInsurance = initialInsuranceAmount > 0;
    const initialInsuranceAmountText = initialHasInsurance ? initialInsuranceAmount.toFixed(2) : '';

    setHasInsurance(initialHasInsurance);
    setInsuranceAmount(initialInsuranceAmountText);

    // Ship date: shipDate from listShipments (top-level) or clientMetadata, or browser current date
    const initialShipDate =
      shipment?.shipDate ||
      shipment?.clientMetadata?.shipDate ||
      shipment?.shipmentShippedAt ||
      format(new Date(), 'yyyy-MM-dd');
    setShipDate(initialShipDate);

    const initialSaturdayDelivery = Boolean(shipment?.enableSaturdayDelivery);
    setEnableSaturdayDelivery(initialSaturdayDelivery);

    const initialSignatureRequired = Boolean(shipment?.signatureRequired);
    setSignatureRequired(initialSignatureRequired);

    initialValuesRef.current = {
      service: initialService,
      packageCode: initialPackageCode,
      dimensions: initialDimensions,
      weightLbs: initialWeightLbs,
      weightOz: initialWeightOz,
      hasInsurance: initialHasInsurance,
      insuranceAmount: initialInsuranceAmountText,
      shipDate: initialShipDate,
      enableSaturdayDelivery: initialSaturdayDelivery,
      signatureRequired: initialSignatureRequired,
    };
    isInitialLoadRef.current = true;

    if (lastOpenedShipmentIdRef.current !== shipment.shipmentId) {
      lastOpenedShipmentIdRef.current = shipment.shipmentId;
      // New shipment opened — clear the persisted-address flag.
      addressPersistedRef.current = false;
      dispatch(
        recordShippingAuditEventThunk({
          eventType: AuditEventType.SHIPMENT_DIALOG_OPENED,
          pomUserId: userAuth.userId,
          performedByName: `${userAuth.firstName ?? ''} ${userAuth.lastName ?? ''}`.trim(),
          shipmentRequestId: shipment.shipmentId,
          referenceId: shipment.referenceId,
        })
      );
    }
  }, [shipment, userAuth.userId, userAuth.firstName, userAuth.lastName, dispatch]);

  // JIT validation removed — validation now happens when the user edits the address
  // or via the validate button. Address validity comes from prx-shipment-service.

  const formInitialShipDate = useMemo(() => {
    const formShipDate = parseLocalDate(shipDate);
    const raw = shipment?.shipDate || shipment?.clientMetadata?.shipDate;
    const shipDateFromApi = parseLocalDate(raw);
    return formShipDate || shipDateFromApi || startOfDay(new Date());
  }, [shipDate, shipment]);

  const enableInsuranceRecommended = useMemo(() => Boolean(shipment?.enableInsurance), [shipment]);

  useEffect(() => {
    if (!shipment?.shipmentId) return;

    const shipmentIsShipped =
      shipment.shipmentStatus === ShipmentStatus.REQUEST_SHIPPED ||
      shipmentLabelStatus === ShipmentStatus.REQUEST_SHIPPED;

    if (shipmentIsShipped) return;
    if (!service && !weightLbs && !weightOz) return;

    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }

    const iv = initialValuesRef.current;
    const hasServiceChanged = service !== iv.service;
    const hasPackageCodeChanged = packageCode !== iv.packageCode;
    const hasDimensionsChanged = dimensions !== iv.dimensions;
    const hasWeightLbsChanged = weightLbs !== iv.weightLbs;
    const hasWeightOzChanged = weightOz !== iv.weightOz;
    const hasInsuranceChanged = hasInsurance !== iv.hasInsurance;
    const hasInsuranceAmountChanged = insuranceAmount !== iv.insuranceAmount;
    const hasShipDateChanged = shipDate !== iv.shipDate;
    const hasSaturdayDeliveryChanged = enableSaturdayDelivery !== iv.enableSaturdayDelivery;
    const hasSignatureRequiredChanged = signatureRequired !== iv.signatureRequired;

    if (
      !hasServiceChanged &&
      !hasPackageCodeChanged &&
      !hasDimensionsChanged &&
      !hasWeightLbsChanged &&
      !hasWeightOzChanged &&
      !hasInsuranceChanged &&
      !hasInsuranceAmountChanged &&
      !hasShipDateChanged &&
      !hasSaturdayDeliveryChanged &&
      !hasSignatureRequiredChanged
    ) {
      debouncedUpdateShipment.cancel();
      if (updateSeqRef.current === 0) {
        // No prior updates — nothing to sync with the backend.
        return;
      }
      // A prior API call may have updated the backend with different values.
      // Fall through to send current (original) values back to the server.
    }

    if (packageCode === 'YOUR_PACKAGING' && !dimensions) {
      debouncedUpdateShipment.cancel();
      // eslint-disable-next-line no-plusplus
      ++updateSeqRef.current;
      setIsUpdatingShipment(false);
      return;
    }

    const lbs = parseFloat(weightLbs) || 0;
    const oz = parseFloat(weightOz) || 0;
    const totalLbs = lbs + oz / 16;

    const currentService = service || '';
    const currentWeightLbs = weightLbs || '';
    const currentWeightOz = weightOz || '';

    const insuranceAmountNumeric =
      hasInsurance && insuranceAmount ? parseFloat(insuranceAmount) || 0 : 0;

    // Build dimensions payload — only include for custom packaging to avoid overwriting with zeros
    let dimensionsPayload;
    if (packageCode === 'YOUR_PACKAGING' && dimensions) {
      const selected = dimensionOptions.find(
        (d) => `${d.length}x${d.width}x${d.height}` === dimensions
      );
      if (selected) {
        dimensionsPayload = {
          length: selected.length,
          width: selected.width,
          height: selected.height,
          units: 'IN',
        };
      }
    }

    const input = {
      shipmentId: shipment.shipmentId,
      referenceId: shipment.referenceId,
      ...(service && { serviceCode: service }),
      ...(packageCode && { packageCode }),
      ...(dimensionsPayload && { dimensions: dimensionsPayload }),
      orderWeight: { value: totalLbs, units: 'LB' },
      insuranceAmountInUSD: insuranceAmountNumeric,
      shipDate: shipDate || format(new Date(), 'yyyy-MM-dd'),
      ...((hasSaturdayDeliveryChanged ||
        (enableSaturdayDelivery && (hasServiceChanged || hasShipDateChanged))) && {
        enableSaturdayDelivery: Boolean(enableSaturdayDelivery),
      }),
      ...(hasSignatureRequiredChanged && {
        signatureRequired: Boolean(signatureRequired),
      }),
    };

    setIsUpdatingShipment(true);

    const targetShipmentId = shipment.shipmentId;
    // eslint-disable-next-line no-plusplus
    const seq = ++updateSeqRef.current;

    const handleUpdateSuccess = (updatedShipment) => {
      if (seq !== updateSeqRef.current) return;
      if (activeShipmentIdRef.current !== targetShipmentId) {
        return;
      }
      let finalService = currentService;
      let finalPackageCode = packageCode;
      let finalDimensions = dimensions;
      let finalWeightLbs = currentWeightLbs;
      let finalWeightOz = currentWeightOz;
      let finalHasInsurance = hasInsurance;
      let finalInsuranceAmountText = insuranceAmount;
      let finalShipDate = shipDate;
      let finalSaturdayDelivery = enableSaturdayDelivery;
      let finalSignatureRequired = signatureRequired;

      if (updatedShipment) {
        if (updatedShipment.serviceCode != null) {
          finalService = updatedShipment.serviceCode;
          setService(updatedShipment.serviceCode);
          const config = servicePackageConfig[updatedShipment.serviceCode];
          if (config) {
            const defaultPackage = config.packages[0];
            const preloadPackage = updatedShipment.packageCode || defaultPackage || '';
            finalPackageCode = preloadPackage;
            setPackageCode(preloadPackage);
          }
        }

        if (updatedShipment.orderWeight) {
          const { value, units } = updatedShipment.orderWeight;
          const weightValue = parseFloat(value) || 0;
          const weightUnits = units?.toLowerCase() || '';

          let totalWeightOz = 0;

          if (['lbs', 'lb', 'pound', 'pounds'].includes(weightUnits)) {
            totalWeightOz = weightValue * 16;
          } else if (['oz', 'ounce', 'ounces'].includes(weightUnits)) {
            totalWeightOz = weightValue;
          } else if (['kg', 'kilogram', 'kilograms'].includes(weightUnits)) {
            totalWeightOz = weightValue * 35.274;
          } else if (['g', 'gram', 'grams'].includes(weightUnits)) {
            totalWeightOz = weightValue * 0.035274;
          }

          const totalLbsFromResponse = Math.floor(totalWeightOz / 16);
          const remainingOzFromResponse = totalWeightOz % 16;
          const ozDisplayFromResponse =
            remainingOzFromResponse > 0
              ? parseFloat(Number(remainingOzFromResponse).toFixed(2)).toString()
              : '';

          finalWeightLbs = totalLbsFromResponse > 0 ? totalLbsFromResponse.toString() : '';
          finalWeightOz = ozDisplayFromResponse;

          setWeightLbs(finalWeightLbs);
          setWeightOz(finalWeightOz);
        }

        if (typeof updatedShipment.insuranceAmountInUSD === 'number') {
          const updatedInsuranceAmount = updatedShipment.insuranceAmountInUSD;
          finalHasInsurance = updatedInsuranceAmount > 0;
          finalInsuranceAmountText = finalHasInsurance ? updatedInsuranceAmount.toFixed(2) : '';

          setHasInsurance(finalHasInsurance);
          setInsuranceAmount(finalInsuranceAmountText);
        }

        if (updatedShipment.cost !== undefined) {
          setUpdatedCost(updatedShipment.cost);
        }

        if (updatedShipment?.clientMetadata?.shipDate) {
          finalShipDate = updatedShipment.clientMetadata.shipDate;
          setShipDate(finalShipDate);
        }

        if (typeof updatedShipment.enableSaturdayDelivery === 'boolean') {
          finalSaturdayDelivery = updatedShipment.enableSaturdayDelivery;
          setEnableSaturdayDelivery(finalSaturdayDelivery);
        }

        if (typeof updatedShipment.signatureRequired === 'boolean') {
          finalSignatureRequired = updatedShipment.signatureRequired;
          setSignatureRequired(finalSignatureRequired);
        }

        // Sync dimensions from API response
        if (updatedShipment.dimensions) {
          const { length, width, height } = updatedShipment.dimensions;
          const dimKey = `${length}x${width}x${height}`;
          if (dimensionOptions.some((d) => `${d.length}x${d.width}x${d.height}` === dimKey)) {
            finalDimensions = dimKey;
            setDimensions(dimKey);
          }
        }
      }

      // Merge original address/service and validated address from update response
      if (
        updatedShipment?.originalShipmentAddress ||
        updatedShipment?.originalServiceCode ||
        updatedShipment?.validatedShipTo ||
        updatedShipment?.tags
      ) {
        setDisplayShipment((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            ...(updatedShipment.serviceCode && {
              serviceCode: updatedShipment.serviceCode,
            }),
            ...(updatedShipment.originalShipmentAddress && {
              originalShipmentAddress: updatedShipment.originalShipmentAddress,
            }),
            ...(updatedShipment.originalServiceCode && {
              originalServiceCode: updatedShipment.originalServiceCode,
            }),
            ...(updatedShipment.validatedShipTo && {
              validatedShipmentAddress: {
                line1: updatedShipment.validatedShipTo.line1,
                line2: updatedShipment.validatedShipTo.line2,
                city: updatedShipment.validatedShipTo.city,
                state: updatedShipment.validatedShipTo.state,
                zip: updatedShipment.validatedShipTo.zip,
                addressValidity: updatedShipment.validatedShipTo.addressValidity,
                addressDeficiencies: updatedShipment.validatedShipTo.addressDeficiencies,
                addressDirectoryId: updatedShipment.validatedShipTo.addressDirectoryId,
              },
            }),
            ...(updatedShipment.tags && { tags: updatedShipment.tags }),
          };
        });
      }

      initialValuesRef.current = {
        service: finalService,
        packageCode: finalPackageCode,
        dimensions: finalDimensions,
        weightLbs: finalWeightLbs,
        weightOz: finalWeightOz,
        hasInsurance: finalHasInsurance,
        insuranceAmount: finalInsuranceAmountText,
        shipDate: finalShipDate,
        enableSaturdayDelivery: finalSaturdayDelivery,
        signatureRequired: finalSignatureRequired,
      };
      updateSeqRef.current = 0;
      setIsUpdatingShipment(false);
    };

    setInlineLabelError('');

    debouncedUpdateShipment({
      dispatch,
      input,
      suppressGlobalError: true,
      onSuccess: handleUpdateSuccess,
      onError: (error) => {
        if (seq !== updateSeqRef.current) return;
        if (activeShipmentIdRef.current !== targetShipmentId) return;
        const prev = initialValuesRef.current;
        updateSeqRef.current = 0;
        setService(prev.service);
        setPackageCode(prev.packageCode);
        setDimensions(prev.dimensions);
        setWeightLbs(prev.weightLbs);
        setWeightOz(prev.weightOz);
        setHasInsurance(prev.hasInsurance);
        setInsuranceAmount(prev.insuranceAmount);
        setShipDate(prev.shipDate);
        setEnableSaturdayDelivery(prev.enableSaturdayDelivery);
        setSignatureRequired(prev.signatureRequired);
        setIsUpdatingShipment(false);
        setInlineLabelError(deriveShipmentErrorMessage(error));
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    service,
    packageCode,
    dimensions,
    weightLbs,
    weightOz,
    hasInsurance,
    insuranceAmount,
    shipDate,
    enableSaturdayDelivery,
    signatureRequired,
    shipment,
    shipmentLabelStatus,
    dispatch,
  ]);
  const onDocumentLoadSuccess = ({ numPages: pages }) => {
    setNumPages(pages);
  };

  const openPdfUrl = () => {
    if (displayShipment?.shipmentId) {
      recordAuditEventApi({
        shipmentId: displayShipment.shipmentId,
        eventType: 'SHIPMENT_LABEL_ACCESSED',
        actionType: 'LABEL_DOWNLOADED',
      });
    }

    dispatch(
      recordShippingAuditEventThunk({
        eventType: AuditEventType.SHIPMENT_LABEL_DOWNLOADED,
        pomUserId: userAuth.userId,
        performedByName: `${userAuth.firstName ?? ''} ${userAuth.lastName ?? ''}`.trim(),
        shipmentRequestId: shipment?.shipmentId,
        referenceId: shipment?.referenceId,
      })
    );

    window.open(pdfBlobUrl, '_blank');
  };

  const handlePrintLabel = async (_, blob = zplBlob) => {
    if (!blob) return;

    const shipmentRequestId = shipment?.shipmentId;
    const referenceId = shipment?.referenceId;
    const performedByName = `${userAuth.firstName ?? ''} ${userAuth.lastName ?? ''}`.trim();
    const printJobId = uuidv4();

    dispatch(
      recordShippingAuditEventThunk({
        eventType: AuditEventType.PRINT_REQUESTED,
        pomUserId: userAuth.userId,
        performedByName,
        shipmentRequestId,
        referenceId,
        correlationId: printJobId,
        status: AuditEventStatus.STARTED,
      })
    );

    const startJSPMWithTimeout = () => {
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          if (JSPrintManager.websocket_status !== WSStatus.Open) {
            reject(new Error('Timeout'));
          }
        }, 1500);

        JSPrintManager.start()
          .then(() => {
            if (JSPrintManager.websocket_status === WSStatus.Open) {
              clearTimeout(timeout);
              resolve();
            } else {
              // Handshake might have failed silently
              clearTimeout(timeout);
              reject(new Error('Handshake failed'));
            }
          })
          .catch((err) => {
            clearTimeout(timeout);
            reject(err);
          });
      });
    };

    if (JSPrintManager.websocket_status !== WSStatus.Open) {
      dispatch(
        recordShippingAuditEventThunk({
          eventType: AuditEventType.PRINTER_DISCOVERY_STARTED,
          pomUserId: userAuth.userId,
          performedByName,
          shipmentRequestId,
          referenceId,
          correlationId: printJobId,
          status: AuditEventStatus.STARTED,
        })
      );
      try {
        enqueueSnackbar({ variant: 'info', message: 'Connecting to printer client...' });
        JSPrintManager.auto_reconnect = false;
        await startJSPMWithTimeout();
      } catch (e) {
        dispatch(
          recordShippingAuditEventThunk({
            eventType: AuditEventType.PRINTER_NOT_FOUND,
            pomUserId: userAuth.userId,
            performedByName,
            shipmentRequestId,
            referenceId,
            correlationId: printJobId,
            status: AuditEventStatus.FAILED,
            metadata: { errorMessage: 'Printer Client Not Installed or Not Running' },
          })
        );

        enqueueSnackbar({
          variant: 'error',
          message: 'Printer Client Not Installed or Not Running',
        });
        return;
      }
    }

    try {
      const printers = await Promise.race([
        JSPrintManager.getPrinters(),
        new Promise((val, reject) => {
          setTimeout(() => reject(new Error('Printer enumeration timeout')), 5000);
        }),
      ]);
      if (!printers || printers.length === 0) {
        dispatch(
          recordShippingAuditEventThunk({
            eventType: AuditEventType.PRINTER_NOT_FOUND,
            pomUserId: userAuth.userId,
            performedByName,
            shipmentRequestId,
            referenceId,
            correlationId: printJobId,
            status: AuditEventStatus.FAILED,
            metadata: { errorMessage: 'Default Printer not configured' },
          })
        );

        enqueueSnackbar({ variant: 'error', message: 'Default Printer not configured' });
        return;
      }

      const printMeta = {
        printerName: printers[0],
        printerCount: printers.length,
        serviceCode: shipment.serviceCode || '',
        shipDate: shipment.shipDate || '',
        weight: shipment.shipmentWeight
          ? `${shipment.shipmentWeight.value} ${shipment.shipmentWeight.units}`
          : '',
      };

      dispatch(
        recordShippingAuditEventThunk({
          eventType: AuditEventType.PRINTER_IDENTIFIED,
          pomUserId: userAuth.userId,
          performedByName,
          shipmentRequestId,
          referenceId,
          correlationId: printJobId,
          metadata: printMeta,
        })
      );

      const cpj = new ClientPrintJob();
      cpj.clientPrinter = new DefaultPrinter();
      const zplString = await blob.text();
      cpj.jobName = `${shipment.shipmentId}.zpl`;
      cpj.printerCommands = zplString;

      const jobMeta = { ...printMeta, jobName: cpj.jobName, zplSize: zplString.length };

      cpj.onUpdated = (data) => {
        if (data.result === 'OK') {
          dispatch(
            recordShippingAuditEventThunk({
              eventType: AuditEventType.PRINT_JOB_COMPLETED,
              pomUserId: userAuth.userId,
              performedByName,
              shipmentRequestId,
              referenceId,
              correlationId: printJobId,
              metadata: jobMeta,
            })
          );
        } else if (data.result === 'Error') {
          dispatch(
            recordShippingAuditEventThunk({
              eventType: AuditEventType.PRINT_JOB_FAILED,
              pomUserId: userAuth.userId,
              performedByName,
              shipmentRequestId,
              referenceId,
              correlationId: printJobId,
              status: AuditEventStatus.FAILED,
              metadata: { ...jobMeta, errorMessage: data.message },
            })
          );
        }
      };

      dispatch(
        recordShippingAuditEventThunk({
          eventType: AuditEventType.PRINT_JOB_SUBMITTED,
          pomUserId: userAuth.userId,
          performedByName,
          shipmentRequestId,
          referenceId,
          correlationId: printJobId,
          metadata: jobMeta,
        })
      );

      cpj.sendToClient();

      // Audit and Success feedback
      if (displayShipment?.shipmentId) {
        recordAuditEventApi({
          shipmentId: displayShipment.shipmentId,
          eventType: 'SHIPMENT_LABEL_ACCESSED',
          actionType: 'LABEL_PRINTED',
        });
      }
      enqueueSnackbar({ variant: 'info', message: 'Print job sent to printer.' });
    } catch (error) {
      dispatch(
        recordShippingAuditEventThunk({
          eventType: AuditEventType.PRINT_JOB_FAILED,
          pomUserId: userAuth.userId,
          performedByName,
          shipmentRequestId,
          referenceId,
          correlationId: printJobId,
          status: AuditEventStatus.FAILED,
          metadata: { errorMessage: error?.message },
        })
      );

      enqueueSnackbar({ variant: 'error', message: 'JSPM Error: Could not send to printer' });
    }
  };

  useEffect(() => {
    if (
      shipment &&
      (shipment.shipmentStatus === ShipmentStatus.REQUEST_SHIPPED ||
        shipmentLabelStatus === ShipmentStatus.REQUEST_SHIPPED)
    ) {
      const labelRetrievalId = uuidv4();
      const labelPerformedByName = `${userAuth.firstName ?? ''} ${userAuth.lastName ?? ''}`.trim();
      dispatch(
        recordShippingAuditEventThunk({
          eventType: AuditEventType.LABEL_RETRIEVAL_STARTED,
          pomUserId: userAuth.userId,
          performedByName: labelPerformedByName,
          shipmentRequestId: shipment.shipmentId,
          referenceId: shipment.referenceId,
          correlationId: labelRetrievalId,
        })
      );
      getShipmentLabelApi({ shipmentId: shipment.shipmentId })
        .then((labelUrl) => {
          dispatch(
            recordShippingAuditEventThunk({
              eventType: AuditEventType.LABEL_RETRIEVAL_COMPLETED,
              pomUserId: userAuth.userId,
              performedByName: labelPerformedByName,
              shipmentRequestId: shipment.shipmentId,
              referenceId: shipment.referenceId,
              correlationId: labelRetrievalId,
            })
          );

          setUpdatedLabelUrl(labelUrl);
        })
        .catch((error) => {
          dispatch(
            recordShippingAuditEventThunk({
              eventType: AuditEventType.LABEL_RETRIEVAL_FAILED,
              pomUserId: userAuth.userId,
              performedByName: labelPerformedByName,
              shipmentRequestId: shipment.shipmentId,
              referenceId: shipment.referenceId,
              correlationId: labelRetrievalId,
              metadata: { errorMessage: error?.message },
            })
          );
        });
    }
  }, [
    shipment,
    shipmentLabelStatus,
    userAuth.userId,
    userAuth.firstName,
    userAuth.lastName,
    dispatch,
  ]);

  useEffect(() => {
    if (!updatedLabelUrl) return undefined;

    let cancelled = false;
    const controller = new AbortController();

    axios
      .get(updatedLabelUrl, { responseType: 'blob', signal: controller.signal })
      .then(async (res) => {
        if (cancelled) return;
        setZplBlob(res.data);
        const pdfUrl = await convertZplBlobToPdfUrl(res.data);
        if (cancelled) return;
        setPdfBlobUrl(pdfUrl);
      })
      .catch((error) => {
        if (cancelled) return;
        dispatch(handleError({ error, heading: 'Failed to retrieve label from S3.' }));
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [updatedLabelUrl, dispatch]);

  useEffect(() => {
    JSPrintManager.auto_reconnect = false;

    return () => {
      if (JSPrintManager.WS && JSPrintManager.websocket_status !== WSStatus.Closed) {
        JSPrintManager?.stop();
      }
    };
  }, []);

  useEffect(() => {
    if (!shipStationShipmentType && shippingOptions.length > 0) {
      const defaultOption = find(shippingOptions, { isDefault: true });
      setShipStationShipmentType(defaultOption.optionCode);
    }
  }, [shipStationShipmentType, shippingOptions]);

  const handleOpenSelectAddress = async () => {
    setIsSelectAddressOpen(true);

    // Always re-fetch on open so the list (and the "Current" match) reflects the latest
    // addresses — e.g. after editing/validating the current address.
    const orderIds = [
      ...new Set(map(displayShipment?.shipmentItems || [], (item) => item.orderId).filter(Boolean)),
    ];

    setIsLoadingRecipientAddresses(true);
    setRecipientAddressesError('');

    try {
      const result = await getRecipientAddressesApi({ orderIds });
      setRecipientAddresses(result || []);
    } catch (error) {
      setRecipientAddressesError('Unable to load recipient addresses. Please try again.');
    } finally {
      setIsLoadingRecipientAddresses(false);
    }
  };

  // Shared refresh applied after any Ship To change (edit dialog or address picker).
  const applyAddressUpdate = (updatedShipment) => {
    if (!updatedShipment) return;
    setDisplayShipment((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        shipmentId: updatedShipment.shipmentId ?? prev.shipmentId,
        referenceId: updatedShipment.referenceId ?? prev.referenceId,
        cost: updatedShipment.cost ?? prev.cost,
        recipientName: updatedShipment.recipientName ?? prev.recipientName,
        shipmentAddress: updatedShipment.shipmentAddress ?? prev.shipmentAddress,
        originalShipmentAddress: updatedShipment.shipmentAddress ?? prev.originalShipmentAddress,
        shipmentPhoneNumber: updatedShipment.shipmentPhoneNumber ?? prev.shipmentPhoneNumber,
        isVerified: updatedShipment.shipmentAddress?.isVerified ?? prev.shipmentAddress?.isVerified,
        validatedShipmentAddress: updatedShipment.validatedShipmentAddress ?? null,
      };
    });
    setSelectedAddressType('input');
    addressPersistedRef.current = true;
    if (typeof updatedShipment.enableSaturdayDelivery === 'boolean') {
      setEnableSaturdayDelivery(updatedShipment.enableSaturdayDelivery);
    }
    if (typeof updatedShipment.signatureRequired === 'boolean') {
      setSignatureRequired(updatedShipment.signatureRequired);
    }
    if (typeof onAfterMutation === 'function') {
      onAfterMutation();
    }
  };

  // "Use Selected Address" — apply the picked address to the shipment's Ship To,
  // using the same updateShipment path as the Edit Ship To dialog.
  const handleUseSelectedAddress = (selectedAddress) => {
    if (!selectedAddress || !displayShipment?.shipmentId) {
      setIsSelectAddressOpen(false);
      return;
    }

    const { address = {} } = selectedAddress;
    const input = {
      shipmentId: displayShipment.shipmentId,
      referenceId: displayShipment.referenceId,
      shipTo: {
        nameOnAddress: selectedAddress.patientName || displayShipment.recipientName,
        phoneNumber: displayShipment.shipmentPhoneNumber || '',
        line1: address.line1,
        line2: address.line2,
        city: address.city,
        state: address.state,
        zip: address.zip,
      },
    };

    setIsSavingRecipientAddress(true);
    // No suppressGlobalError: a failed update surfaces the app's global error dialog.
    dispatch(updateShipment({ input }))
      .then((updated) => {
        if (!updated || !updated.shipTo) {
          return;
        }
        applyAddressUpdate({
          shipmentId: updated.shipmentId,
          referenceId: updated.referenceId,
          cost: updated.cost,
          recipientName: updated.shipTo.nameOnAddress,
          shipmentAddress: {
            line1: updated.shipTo.line1,
            line2: updated.shipTo.line2,
            city: updated.shipTo.city,
            state: updated.shipTo.state,
            zip: updated.shipTo.zip,
            isVerified: updated.shipTo.isVerified,
            addressValidity: updated.shipTo.addressValidity,
            addressDeficiencies: updated.shipTo.addressDeficiencies,
            addressDirectoryId: updated.shipTo.addressDirectoryId,
          },
          shipmentPhoneNumber: updated.shipTo.phoneNumber,
        });
        enqueueSnackbar({ variant: 'success', message: 'Shipment address updated' });
      })
      .catch(() => {
        // Global error dialog is shown by the thunk; nothing to do inline.
      })
      .finally(() => {
        setIsSavingRecipientAddress(false);
        // Close the drawer so the global error dialog (or success) isn't hidden behind it.
        setIsSelectAddressOpen(false);
      });
  };

  if (!displayShipment) {
    return null;
  }

  const {
    referenceId,
    shipmentId,
    recipientName,
    shipmentAddress,
    partnerName,
    practiceName,
    shipmentStatus,
    serviceCode,
    shipmentItems,
    isBatchShipment,
    isManualShipment,
    carrierCode: shipmentCarrierCode,
    cost,
    trackingNumber,
    shipmentShippedAt,
    fulfillingPharmacyId,
    originalShipmentAddress,
    originalServiceCode,
    deliveryInstructions,
    validatedShipmentAddress,
    tags,
  } = displayShipment;

  const hasOriginalServiceCode = originalServiceCode && originalServiceCode !== serviceCode;

  // "Choose Saved Address" needs an order to resolve the patient. A manual shipment
  // with an item moved in from a real order still has an orderId, so gate on the data,
  // not the isManualShipment flag.
  const hasResolvableOrder = (shipmentItems || []).some((item) => item.orderId);
  // "Choose Saved Address" gating: a batch shipment with multiple items may span different
  // recipients, so saved-address selection isn't safe. Reason shown as a tooltip when disabled.
  const isMultiItemBatchShipment = isBatchShipment && (shipmentItems || []).length > 1;
  const chooseSavedAddressDisabled = !hasResolvableOrder || isMultiItemBatchShipment;
  const chooseSavedAddressDisabledReason = !hasResolvableOrder
    ? 'This shipment has no linked order, so the saved addresses cannot be resolved.'
    : 'Multiple recipients are associated with this shipment, so recipient addresses cannot be displayed.';

  const hasValidatedAddress =
    originalShipmentAddress &&
    validatedShipmentAddress &&
    ((originalShipmentAddress.line1 || '') !== (validatedShipmentAddress.line1 || '') ||
      (originalShipmentAddress.line2 || '') !== (validatedShipmentAddress.line2 || '') ||
      (originalShipmentAddress.city || '') !== (validatedShipmentAddress.city || '') ||
      (originalShipmentAddress.state || '') !== (validatedShipmentAddress.state || '') ||
      (originalShipmentAddress.zip || '') !== (validatedShipmentAddress.zip || ''));

  const displayShippedAt = shipmentShippedAt || updatedShippedAt;

  const getDisplayCost = () => {
    const value = Number(updatedCost ?? cost ?? 0);
    if (Number.isNaN(value)) return '-';
    return value > 0 ? value.toFixed(2) : '-';
  };

  const getDisplayInsuranceAmount = () => {
    const value = Number(insuranceAmount) ?? Number(shipment?.insuranceAmountInUSD ?? 0);
    if (Number.isNaN(value) || value <= 0) return '-';
    return value > 0 ? value.toFixed(2) : '-';
  };

  const minShipDate = startOfDay(subDays(new Date(), 1));
  const parsedShipDate = parseLocalDate(shipDate);
  const isShipDateValid =
    Boolean(parsedShipDate) && parsedShipDate.getTime() > minShipDate.getTime();

  const handleClose = () => {
    onClose();
  };

  const handleAddressToggle = (event) => {
    setSelectedAddressType(event.target.value);
    // A genuine input/validated choice — allow the pre-label sync to push it.
    addressPersistedRef.current = false;
  };

  const refreshAfterShipmentMutation = async () => {
    if (onAfterMutation) {
      try {
        await onAfterMutation();
      } catch (error) {
        dispatch(handleError({ error }));
      }
    }
  };

  const handleIncrementLbs = () => {
    const current = parseFloat(weightLbs) || 0;
    setWeightLbs((current + 1).toString());
  };

  const handleDecrementLbs = () => {
    const current = parseFloat(weightLbs) || 0;
    if (current > 0) {
      setWeightLbs((current - 1).toString());
    }
  };

  const handleIncrementOz = () => {
    const current = parseFloat(weightOz) || 0;
    if (current < 15) {
      setWeightOz((current + 1).toString());
    } else {
      const lbs = parseFloat(weightLbs) || 0;
      setWeightLbs((lbs + 1).toString());
      setWeightOz('0');
    }
  };

  const handleDecrementOz = () => {
    const current = parseFloat(weightOz) || 0;
    if (current > 0) {
      setWeightOz((current - 1).toString());
    } else {
      const lbs = parseFloat(weightLbs) || 0;
      if (lbs > 0) {
        setWeightLbs((lbs - 1).toString());
        setWeightOz('15');
      }
    }
  };

  const handleConfirmSendToShipStation = () => {
    setIsSendingToShipStation(true);
    const lineItemIds = map(shipmentItems, (item) => item.lineItemId);

    dispatch(
      createOrderShipment({
        lineItemIds,
        shipmentMode: shipmentMode.SHIP_VIA_SHIPSTATION,
        shipmentType: shipStationShipmentType,
        isShipmentFromPrx: true,
        addressDirectoryId: shipmentAddress.addressDirectoryId,
        ...(isBatchShipment && { isBatchShipment: true }),
        ...(isBatchShipment && {
          nameOnAddress: recipientName,
        }),
      })
    ).then(async (success) => {
      if (success) {
        enqueueSnackbar('Shipment created in ShipStation', { variant: 'success' });
        await refreshAfterShipmentMutation();
        handleClose();
      }
      setIsSendingToShipStation(false);
      setIsSendToShipstationDialogOpen(false);
    });
  };

  const handleOpenSendToShipStationDialog = () => {
    setIsSendToShipstationDialogOpen(true);
  };

  const handleCloseSendToShipStationDialog = () => {
    setIsSendToShipstationDialogOpen(false);
  };

  const handleCreateManualShipment = (data) => {
    setIsSendingToShipStation(true);
    const lineItemIds = map(shipmentItems, (item) => item.lineItemId);
    const shipDateStr = data?.shipDate ? format(new Date(data.shipDate), 'yyyy-MM-dd') : null;
    const shipTimeStr = data?.shipTime || null;

    dispatch(
      createOrderShipment({
        lineItemIds,
        shipmentMode: shipmentMode.EXTERNAL_SHIPMENT,
        shipmentType: serviceCode,
        carrierCode: shipmentCarrierCode,
        trackingNumber: data?.trackingNumber,
        externalShipmentId: data?.externalShipmentId || uuidV4(),
        shipDate: shipDateStr,
        shipTime: shipTimeStr,
        isShipmentFromPrx: true,
        addressDirectoryId: shipmentAddress.addressDirectoryId,
        ...(isBatchShipment && { isBatchShipment: true }),
      })
    )
      .then(async (success) => {
        if (success) {
          enqueueSnackbar('Shipment shipped successfully', { variant: 'success' });
          await refreshAfterShipmentMutation();
          handleClose();
        }
        setIsSendingToShipStation(false);
      })
      .catch(() => {
        enqueueSnackbar('Failed to ship shipment', { variant: 'error' });
        setIsSendingToShipStation(false);
      });
  };

  const handleCreateLabel = async () => {
    if (!shipmentId) {
      enqueueSnackbar('Shipment order ID is missing', { variant: 'error' });
      return;
    }

    dispatch(workQueueActions.labelGenerationStarted({ shipmentId }));
    // Clear any prior inline warning before retrying label creation.
    setInlineLabelError('');

    try {
      // Step 1: Cancel the pending debounce timer to prevent a future updateShipment
      // from firing after our synchronous one completes (which would cause a redundant call).
      // We do NOT need to abort an already in-flight HTTP request — the backend uses
      // pessimistic locking with a TOCTOU guard (modifiedTimestamp check), so concurrent
      // calls resolve safely.
      debouncedUpdateShipment.cancel();
      setIsUpdatingShipment(false);

      // Step 2: Check if any field has changed since the last save
      const iv = initialValuesRef.current;
      const hasConfigChanges =
        service !== iv.service ||
        packageCode !== iv.packageCode ||
        dimensions !== iv.dimensions ||
        weightLbs !== iv.weightLbs ||
        weightOz !== iv.weightOz ||
        hasInsurance !== iv.hasInsurance ||
        insuranceAmount !== iv.insuranceAmount ||
        shipDate !== iv.shipDate ||
        enableSaturdayDelivery !== iv.enableSaturdayDelivery ||
        signatureRequired !== iv.signatureRequired;

      // Sync the radio-selected address to the backend before generating the label
      // (radio toggle is frontend-only). Handles both validated→input and input→validated switches.
      const addressToSync =
        selectedAddressType === 'validated' ? validatedShipmentAddress : originalShipmentAddress;
      const needsAddressSync =
        !addressPersistedRef.current &&
        addressToSync &&
        ((addressToSync.line1 || '') !== (shipmentAddress.line1 || '') ||
          (addressToSync.line2 || '') !== (shipmentAddress.line2 || '') ||
          (addressToSync.city || '') !== (shipmentAddress.city || '') ||
          (addressToSync.state || '') !== (shipmentAddress.state || '') ||
          (addressToSync.zip || '') !== (shipmentAddress.zip || ''));

      let updatedShipment = null;

      if (hasConfigChanges || needsAddressSync) {
        dispatch(
          workQueueActions.labelProgressUpdated({ shipmentId, progress: 'Saving config...' })
        );

        // Step 3: Build current config payload (same shape as the useEffect debounce)
        const lbs = parseFloat(weightLbs) || 0;
        const oz = parseFloat(weightOz) || 0;
        const syncTotalLbs = lbs + oz / 16;
        const insuranceAmountNumeric =
          hasInsurance && insuranceAmount ? parseFloat(insuranceAmount) || 0 : 0;

        let dimensionsPayload;
        if (packageCode === 'YOUR_PACKAGING' && dimensions) {
          const selected = dimensionOptions.find(
            (d) => `${d.length}x${d.width}x${d.height}` === dimensions
          );
          if (selected) {
            dimensionsPayload = {
              length: selected.length,
              width: selected.width,
              height: selected.height,
              units: 'IN',
            };
          }
        }

        const syncInput = {
          shipmentId: shipment.shipmentId,
          referenceId: shipment.referenceId,
          ...(service && { serviceCode: service }),
          ...(packageCode && { packageCode }),
          ...(dimensionsPayload && { dimensions: dimensionsPayload }),
          orderWeight: { value: syncTotalLbs, units: 'LB' },
          insuranceAmountInUSD: insuranceAmountNumeric,
          shipDate: shipDate || format(new Date(), 'yyyy-MM-dd'),
          ...((enableSaturdayDelivery !== iv.enableSaturdayDelivery ||
            (enableSaturdayDelivery && (service !== iv.service || shipDate !== iv.shipDate))) && {
            enableSaturdayDelivery: Boolean(enableSaturdayDelivery),
          }),
          ...(signatureRequired !== iv.signatureRequired && {
            signatureRequired: Boolean(signatureRequired),
          }),
          // Sync selected address before label generation — radio toggle is frontend-only
          ...(needsAddressSync && {
            shipTo: {
              nameOnAddress: recipientName || '',
              phoneNumber: displayShipment.shipmentPhoneNumber || '',
              line1: addressToSync.line1,
              line2: addressToSync.line2 || '',
              city: addressToSync.city,
              state: addressToSync.state,
              zip: addressToSync.zip,
            },
            // Audit flag — true when shipping with the unvalidated original input address
            inputAddressSelected: selectedAddressType === 'input',
          }),
        };

        // Step 4: Sync config to backend (non-debounced, await completion).
        // POM-5507: suppress the global error dialog — any failure here is
        // rethrown and surfaced inline by this function's catch block.
        updatedShipment = await dispatch(
          updateShipment({ input: syncInput, suppressGlobalError: true })
        );

        // Step 5: Continue even if dialog was closed — operation runs in background
      }

      // Helper: is this dialog still the active one for this shipment?
      const isDialogActive = () => activeShipmentIdRef.current === shipment.shipmentId;

      // Step 6: Update component state and initialValuesRef only when dialog is still active
      if (updatedShipment && isDialogActive()) {
        let syncService = service;
        let syncPackageCode = packageCode;
        let syncDimensions = dimensions;
        const syncWeightLbs = weightLbs || '';
        const syncWeightOz = weightOz || '';

        if (updatedShipment.serviceCode != null) {
          syncService = updatedShipment.serviceCode;
          setService(updatedShipment.serviceCode);
          const config = servicePackageConfig[updatedShipment.serviceCode];
          if (config) {
            const defaultPackage = config.packages[0];
            syncPackageCode = updatedShipment.packageCode || defaultPackage || '';
            setPackageCode(syncPackageCode);
          }
        }
        if (updatedShipment.dimensions) {
          const { length, width, height } = updatedShipment.dimensions;
          const dimKey = `${length}x${width}x${height}`;
          if (dimensionOptions.some((d) => `${d.length}x${d.width}x${d.height}` === dimKey)) {
            syncDimensions = dimKey;
            setDimensions(dimKey);
          }
        }
        if (updatedShipment.cost !== undefined) {
          setUpdatedCost(updatedShipment.cost);
        }

        // Merge synced address, original address/service, and validated address from update response
        if (
          updatedShipment?.shipTo ||
          updatedShipment?.originalShipmentAddress ||
          updatedShipment?.originalServiceCode ||
          updatedShipment?.validatedShipTo
        ) {
          setDisplayShipment((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              ...(updatedShipment.serviceCode && {
                serviceCode: updatedShipment.serviceCode,
              }),
              // Update shipmentAddress from the sync response so the shipped view
              // immediately reflects the address that was actually shipped with
              ...(updatedShipment.shipTo && {
                shipmentAddress: {
                  ...prev.shipmentAddress,
                  line1: updatedShipment.shipTo.line1,
                  line2: updatedShipment.shipTo.line2,
                  city: updatedShipment.shipTo.city,
                  state: updatedShipment.shipTo.state,
                  zip: updatedShipment.shipTo.zip,
                  isVerified: updatedShipment.shipTo.isVerified,
                  addressValidity: updatedShipment.shipTo.addressValidity,
                  addressDeficiencies: updatedShipment.shipTo.addressDeficiencies,
                  addressDirectoryId: updatedShipment.shipTo.addressDirectoryId,
                },
              }),
              ...(updatedShipment.originalShipmentAddress && {
                originalShipmentAddress: updatedShipment.originalShipmentAddress,
              }),
              ...(updatedShipment.originalServiceCode && {
                originalServiceCode: updatedShipment.originalServiceCode,
              }),
              ...(updatedShipment.validatedShipTo && {
                validatedShipmentAddress: {
                  line1: updatedShipment.validatedShipTo.line1,
                  line2: updatedShipment.validatedShipTo.line2,
                  city: updatedShipment.validatedShipTo.city,
                  state: updatedShipment.validatedShipTo.state,
                  zip: updatedShipment.validatedShipTo.zip,
                  addressValidity: updatedShipment.validatedShipTo.addressValidity,
                  addressDeficiencies: updatedShipment.validatedShipTo.addressDeficiencies,
                  addressDirectoryId: updatedShipment.validatedShipTo.addressDirectoryId,
                },
              }),
            };
          });
        }

        initialValuesRef.current = {
          service: syncService,
          packageCode: syncPackageCode,
          dimensions: syncDimensions,
          weightLbs: syncWeightLbs,
          weightOz: syncWeightOz,
          hasInsurance,
          insuranceAmount,
          shipDate: shipDate || format(new Date(), 'yyyy-MM-dd'),
          enableSaturdayDelivery,
          signatureRequired,
        };
      }

      // Step 7: Generate label
      dispatch(
        workQueueActions.labelProgressUpdated({ shipmentId, progress: 'Generating label...' })
      );

      const generatedLabelData = await generateLabelForShipmentApi({ shipmentId });

      // Step 8: Update component state only if this dialog is still the active one;
      // download and print always continue in the background.
      if (isDialogActive()) {
        // Update package details from API response
        setService(generatedLabelData.carrierServiceCode);
        setPackageCode(generatedLabelData.packageCode);

        // Sync dimensions from generate label response only if package requires them
        if (generatedLabelData.packageCode === 'YOUR_PACKAGING' && generatedLabelData.dimensions) {
          const { length, width, height } = generatedLabelData.dimensions;
          const dimKey = `${length}x${width}x${height}`;
          if (dimensionOptions.some((d) => `${d.length}x${d.width}x${d.height}` === dimKey)) {
            setDimensions(dimKey);
          } else {
            setDimensions('');
          }
        } else {
          setDimensions('');
        }

        // Update weight
        const { value, units } = generatedLabelData.orderWeight;
        const weightValue = parseFloat(value) || 0;
        const weightUnits = units?.toLowerCase() || '';

        let totalWeightOz = 0;

        if (['lbs', 'lb', 'pound', 'pounds'].includes(weightUnits)) {
          totalWeightOz = weightValue * 16;
        }
        if (['oz', 'ounce', 'ounces'].includes(weightUnits)) {
          totalWeightOz = weightValue;
        }
        if (['kg', 'kilogram', 'kilograms'].includes(weightUnits)) {
          totalWeightOz = weightValue * 35.274;
        }
        if (['g', 'gram', 'grams'].includes(weightUnits)) {
          totalWeightOz = weightValue * 0.035274;
        }

        const totalLbs = Math.floor(totalWeightOz / 16);
        const remainingOz = totalWeightOz % 16;
        const ozDisplay =
          remainingOz > 0 ? parseFloat(Number(remainingOz).toFixed(2)).toString() : '';

        setWeightLbs(totalLbs > 0 ? totalLbs.toString() : '');
        setWeightOz(ozDisplay);

        // Update shipment status and details
        setShipmentLabelStatus(generatedLabelData.status);
        setUpdatedTrackingNumber(generatedLabelData.trackingNumber);
        setUpdatedShippedAt(generatedLabelData.shippedTimestamp);
        setUpdatedLabelUrl(generatedLabelData.labelUrl);
        if (typeof generatedLabelData.cost === 'number') {
          setUpdatedCost(generatedLabelData.cost);
        }
        setInsuranceAmount(generatedLabelData.insuranceAmountInUSD);
      }

      dispatch(
        workQueueActions.labelProgressUpdated({ shipmentId, progress: 'Downloading label...' })
      );

      await axios
        .get(generatedLabelData.labelUrl, { responseType: 'blob' })
        .then(async (res) => {
          if (isDialogActive()) {
            setZplBlob(res.data);
          }
          dispatch(
            workQueueActions.labelProgressUpdated({ shipmentId, progress: 'Sending to printer...' })
          );
          await handlePrintLabel(undefined, res.data);
        })
        .catch((error) => {
          dispatch(handleError({ error, heading: 'Failed to retrieve label from S3.' }));
        });

      dispatch(workQueueActions.labelProgressUpdated({ shipmentId, progress: 'Refreshing...' }));
      await refreshAfterShipmentMutation();
    } catch (error) {
      setInlineLabelError(deriveShipmentErrorMessage(error));
    } finally {
      dispatch(workQueueActions.labelGenerationFinished({ shipmentId }));
    }
  };

  const totalWeightLbs = (parseFloat(weightLbs) || 0) + (parseFloat(weightOz) || 0) / 16;
  const isWeightEntered = parseFloat(weightLbs) >= 1 || parseFloat(weightOz) >= 1;
  const isWeightOver50 = totalWeightLbs > 50;
  const isValidWeight = isWeightEntered && !isWeightOver50;

  const insuranceAmountError = hasInsurance
    ? validateRequired('Insurance amount is required')(insuranceAmount) ||
      validateGreaterThan('Insurance amount must be greater than 0', 0)(insuranceAmount)
    : undefined;

  // POM-5299 — block voiding once the carrier has the package (backend enforces the
  // same rule; this is the UX guard).
  const { isPickedUp: isShipmentPickedUp, tooltip: voidLabelTooltip } =
    getPickupVoidRestriction(shipment);
  const isVoidLabelDisabled = disableVoidLabel || isShipmentPickedUp;

  const handleVoidLabel = async () => {
    const trackingNum = trackingNumber || updatedTrackingNumber;
    if (!trackingNum) {
      enqueueSnackbar('Tracking number is missing', { variant: 'error' });
      return;
    }

    setIsVoidingLabel(true);
    try {
      await cancelPrxShipmentApi({ trackingNumber: trackingNum });
      enqueueSnackbar('Label voided successfully', { variant: 'success' });
      setVoidLabelDialogOpen(false);
      await refreshAfterShipmentMutation();
      onClose();
    } catch (error) {
      dispatch(handleError({ error }));
      // Surface the server-provided reason (e.g. the carrier-pickup restriction) instead
      // of a generic message so the user understands why the void was rejected.
      const serverMessage =
        error?.response?.errors?.[0]?.message || error?.message || 'Failed to void label';
      enqueueSnackbar(serverMessage, { variant: 'error' });
    } finally {
      setIsVoidingLabel(false);
    }
  };

  const handleCancelShipment = async ({ cancelOrder, reason } = {}) => {
    if (!shipmentId) {
      enqueueSnackbar('Shipment order ID is missing', { variant: 'error' });
      return;
    }

    setIsCancellingShipment(true);

    dispatch(deletePrxShipment({ shipmentId, cancelOrder, reason }))
      .then(async () => {
        const message = cancelOrder
          ? 'Shipment and order cancelled successfully'
          : 'Shipment cancelled successfully';
        enqueueSnackbar(message, { variant: 'success' });
        setCancelShipmentDialogOpen(false);
        await refreshAfterShipmentMutation();
        onClose();
      })
      .catch((error) => {
        dispatch(handleError({ error }));
        enqueueSnackbar('Failed to cancel shipment', { variant: 'error' });
      })
      .finally(() => {
        setIsCancellingShipment(false);
      });
  };

  const getFulfillingPharmacyName = () => {
    if (!fulfillingPharmacyId) return '—';
    const selectedPharmacy = find(
      fulfillingPharmacies,
      (pharmacy) => pharmacy.fulfillingPharmacyId === fulfillingPharmacyId
    );

    const facilityState = find(
      facilities,
      (facility) => facility.fulfillingPharmacyId === fulfillingPharmacyId
    )?.state;
    return facilityState
      ? `${selectedPharmacy.fulfillingPharmacyName} - ${facilityState}`
      : selectedPharmacy.fulfillingPharmacyName;
  };

  const isShipped =
    shipmentStatus === ShipmentStatus.REQUEST_SHIPPED ||
    shipmentLabelStatus === ShipmentStatus.REQUEST_SHIPPED;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        fullWidth
        maxWidth='lg'
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[10],
            bgcolor: 'grey.50',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '80vh',
          },
        }}
      >
        {open && (
          <ShipmentDetailHotkeys
            handleCreateLabel={
              !isShipped &&
              shippingService === 'FedEx' &&
              !isCreatingLabel &&
              (shipmentAddress?.addressValidity || shipmentAddress?.isVerified) &&
              isShipDateValid &&
              isValidWeight &&
              !(packageCode === 'YOUR_PACKAGING' && !dimensions)
                ? handleCreateLabel
                : undefined
            }
            handlePrintLabel={pdfBlobUrl ? handlePrintLabel : undefined}
            handleChangeService={
              !isShipped && !isCreatingLabel
                ? (newService) => {
                    if (service !== newService) {
                      setService(newService);
                      const config = servicePackageConfig[newService];
                      if (config) setPackageCode(config.packages[0]);
                      setDimensions('');
                    }
                  }
                : undefined
            }
          />
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            py: 2,
            bgcolor: 'primary.light',
            borderBottom: `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <RiShip2Fill size={28} color={perfectRxTheme.palette.text.light} />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant='h6' color={perfectRxTheme.palette.text.light} fontWeight={600}>
                  Order: {referenceId}
                </Typography>
                <Tooltip title='View Shipment History'>
                  <IconButton
                    onClick={() => setTimelineOpen(true)}
                    aria-label='View Shipment History'
                    sx={{
                      color: perfectRxTheme.palette.text.light,
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                    }}
                  >
                    <HistoryIcon />
                  </IconButton>
                </Tooltip>
                {isManualShipment && (
                  <Chip
                    label='Manual Shipment'
                    size='small'
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      height: 30,
                      bgcolor: '#E3F2FD',
                      color: '#1565C0',
                      border: 'none',
                    }}
                  />
                )}
              </Box>
              <Typography
                variant='caption'
                color={perfectRxTheme.palette.text.light}
                sx={{ opacity: 0.9 }}
              >
                Shipment Details & Configuration
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {shipment?.shipmentId && <ShipmentRoomUsers shipmentId={shipment.shipmentId} />}
            <IconButton
              onClick={handleClose}
              aria-label='Close Dialog'
              sx={{
                color: perfectRxTheme.palette.text.light,
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ px: 20, py: 5, bgcolor: 'grey.50', flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Grid container spacing={3.5}>
            {/* Left Panel - Order and Shipment Details */}
            <Grid item xs={12} md={shippingService === 'Shipstation' ? 12 : 8}>
              {/* Shipping Information Card */}
              <Card
                sx={{
                  mb: 3.5,
                  boxShadow: theme.shadows[2],
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  p: 3.5,
                  bgcolor: 'background.paper',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                  }}
                >
                  {/* 40% - Status and Address */}
                  <Box sx={{ flex: '0 0 40%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Chip
                        label={isShipped ? 'Shipped' : 'Ready to Ship'}
                        sx={{
                          mb: 2.5,
                          fontWeight: 600,
                          height: 32,
                          fontSize: '0.875rem',
                          px: 1.5,
                          borderRadius: 1,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          ...(isShipped
                            ? { bgcolor: 'primary.light', color: '#FFFFFF', border: 'none' }
                            : { bgcolor: '#FF9800', color: '#FFFFFF', border: 'none' }),
                        }}
                      />
                      {shipmentStatus === ShipmentStatus.REQUEST_CREATED && (
                        <Button
                          onClick={() => setCancelShipmentDialogOpen(true)}
                          color='error'
                          variant='outlined'
                          disabled={isCreatingLabel}
                        >
                          Cancel
                        </Button>
                      )}
                    </Box>
                    <Typography variant='body1' fontWeight={600} sx={{ mb: 0.75 }}>
                      Ship To Address
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        wordBreak: 'break-word',
                        mb: 2.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOnIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
                        <Typography variant='h6' fontWeight={600}>
                          {recipientName
                            ? casingFormat(recipientName)
                            : 'Recipient Name Not Available'}
                        </Typography>
                        {!isShipped && (
                          <>
                            <IconButton
                              size='small'
                              onClick={(event) => setAddressMenuAnchor(event.currentTarget)}
                              disabled={isValidatingAddress || isCreatingLabel}
                              aria-label='Address options'
                              sx={{ color: 'primary.main' }}
                            >
                              <MoreVertIcon fontSize='small' />
                            </IconButton>
                            <Menu
                              anchorEl={addressMenuAnchor}
                              open={Boolean(addressMenuAnchor)}
                              onClose={() => setAddressMenuAnchor(null)}
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                              PaperProps={{
                                sx: {
                                  minWidth: 208,
                                  borderRadius: 2,
                                  mt: 0.5,
                                  boxShadow: '0 6px 20px rgba(16, 24, 40, 0.12)',
                                },
                              }}
                            >
                              <MenuItem
                                onClick={() => {
                                  setAddressMenuAnchor(null);
                                  setIsEditAddressDialogOpen(true);
                                }}
                                sx={{ fontSize: '0.8125rem', py: 0.9 }}
                              >
                                <EditIcon
                                  sx={{ fontSize: '1.05rem', mr: 1.25, color: 'primary.main' }}
                                />
                                Edit Current Address
                              </MenuItem>
                              {shipmentStatus === ShipmentStatus.REQUEST_CREATED && (
                                <Tooltip
                                  title={
                                    chooseSavedAddressDisabled
                                      ? chooseSavedAddressDisabledReason
                                      : ''
                                  }
                                  placement='left'
                                >
                                  {/* span so the tooltip works while the MenuItem is disabled */}
                                  <span>
                                    <MenuItem
                                      onClick={() => {
                                        setAddressMenuAnchor(null);
                                        handleOpenSelectAddress();
                                      }}
                                      disabled={chooseSavedAddressDisabled}
                                      sx={{ fontSize: '0.8125rem', py: 0.9 }}
                                    >
                                      <LocationOnIcon
                                        sx={{
                                          fontSize: '1.05rem',
                                          mr: 1.25,
                                          color: 'primary.main',
                                        }}
                                      />
                                      Choose Saved Address
                                    </MenuItem>
                                  </span>
                                </Tooltip>
                              )}
                            </Menu>
                          </>
                        )}
                      </Box>
                    </Box>
                    <SelectPatientAddressDialog
                      open={isSelectAddressOpen}
                      onClose={() => {
                        if (isSavingRecipientAddress) return;
                        setIsSelectAddressOpen(false);
                      }}
                      recipientAddresses={recipientAddresses}
                      isLoading={isLoadingRecipientAddresses}
                      error={recipientAddressesError}
                      isSaving={isSavingRecipientAddress}
                      currentShipTo={{
                        recipientName: displayShipment?.recipientName,
                        address: displayShipment?.shipmentAddress,
                      }}
                      onUseAddress={handleUseSelectedAddress}
                    />
                    {/* Address display with radio toggle when validated address differs */}
                    {hasValidatedAddress && !isShipped ? (
                      <RadioGroup
                        value={selectedAddressType}
                        onChange={handleAddressToggle}
                        sx={{ gap: 0.5 }}
                      >
                        <FormControlLabel
                          value='input'
                          control={<Radio size='small' />}
                          disabled={isCreatingLabel}
                          label={
                            <Typography variant='body2' fontWeight={600}>
                              Original Address
                            </Typography>
                          }
                          sx={{ mb: 0 }}
                        />
                        <Box
                          sx={{
                            pl: 3.5,
                            ml: 3.5,
                            mb: 1.5,
                            borderLeft: `3px solid ${
                              selectedAddressType === 'input'
                                ? theme.palette.primary.main
                                : theme.palette.grey[400]
                            }`,
                          }}
                        >
                          <Typography variant='body1' sx={{ mb: 0.5 }}>
                            {originalShipmentAddress.line1}
                          </Typography>
                          {originalShipmentAddress.line2 && (
                            <Typography variant='body1' sx={{ mb: 0.5 }}>
                              {originalShipmentAddress.line2}
                            </Typography>
                          )}
                          <Typography variant='body1' color='text.secondary'>
                            {[
                              originalShipmentAddress.city,
                              originalShipmentAddress.state,
                              originalShipmentAddress.zip,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </Typography>
                        </Box>

                        {selectedAddressType === 'input' && (
                          <Alert
                            severity='info'
                            sx={{
                              mt: 0.5,
                              mb: 1.5,
                              ml: 3.5,
                              borderRadius: 1.5,
                              '& .MuiAlert-message': { width: '100%' },
                            }}
                          >
                            <AlertTitle sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 0.25 }}>
                              Unvalidated Address Selected
                            </AlertTitle>
                            <Typography variant='body2'>
                              This is the original address from the order and has not been validated
                              by FedEx.
                            </Typography>
                          </Alert>
                        )}

                        <FormControlLabel
                          value='validated'
                          control={<Radio size='small' />}
                          disabled={isCreatingLabel}
                          label={
                            <Typography variant='body2' fontWeight={600}>
                              FedEx Validated Address
                            </Typography>
                          }
                          sx={{ mb: 0 }}
                        />
                        <Box
                          sx={{
                            pl: 3.5,
                            ml: 3.5,
                            borderLeft: `3px solid ${
                              selectedAddressType === 'validated'
                                ? theme.palette.primary.main
                                : theme.palette.grey[400]
                            }`,
                          }}
                        >
                          <Typography variant='body1' sx={{ mb: 0.5 }}>
                            {validatedShipmentAddress.line1}
                          </Typography>
                          {validatedShipmentAddress.line2 && (
                            <Typography variant='body1' sx={{ mb: 0.5 }}>
                              {validatedShipmentAddress.line2}
                            </Typography>
                          )}
                          <Typography variant='body1' color='text.secondary'>
                            {[
                              validatedShipmentAddress.city,
                              validatedShipmentAddress.state,
                              validatedShipmentAddress.zip,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </Typography>
                        </Box>
                      </RadioGroup>
                    ) : (
                      <Box
                        sx={{
                          pl: 3.5,
                          borderLeft: `3px solid ${theme.palette.primary.main}`,
                        }}
                      >
                        <Typography variant='body1' sx={{ mb: 0.75 }}>
                          {shipmentAddress.line1}
                        </Typography>
                        {shipmentAddress.line2 && (
                          <Typography variant='body1' sx={{ mb: 0.75 }}>
                            {shipmentAddress?.line2}
                          </Typography>
                        )}
                        <Typography variant='body1' color='text.secondary' sx={{ mt: 0.5 }}>
                          {[shipmentAddress.city, shipmentAddress.state, shipmentAddress.zip]
                            .filter(Boolean)
                            .join(', ')}
                        </Typography>
                        {/* Address Verification Status */}
                        {!isShipped && (
                          <>
                            {/* Validating spinner */}
                            {isValidatingAddress && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                                <CircularProgress size={16} sx={{ color: 'primary.main' }} />
                                <Typography variant='body2' color='text.secondary' fontWeight={500}>
                                  Verifying address...
                                </Typography>
                              </Box>
                            )}

                            {/* VALID */}
                            {!isValidatingAddress &&
                              shipmentAddress.addressValidity === 'VALID' && (
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}
                                >
                                  <CheckCircleIcon
                                    sx={{ color: 'success.main', fontSize: '1.1rem' }}
                                  />
                                  <Typography variant='body2' color='success.main' fontWeight={500}>
                                    Address Verified
                                  </Typography>
                                </Box>
                              )}

                            {/* WARN — attention-capturing alert box */}
                            {!isValidatingAddress && shipmentAddress.addressValidity === 'WARN' && (
                              <Alert
                                severity='warning'
                                icon={
                                  <WarningAmberIcon
                                    fontSize='inherit'
                                    sx={{ color: perfectRxTheme.palette.warning.warningColour }}
                                  />
                                }
                                role='alert'
                                sx={{
                                  mt: 1.5,
                                  borderRadius: 1.5,
                                  bgcolor: `${perfectRxTheme.palette.warning.warningColour}15`,
                                  color: theme.palette.text.primary,
                                  '& .MuiAlert-message': { width: '100%' },
                                }}
                              >
                                <AlertTitle
                                  sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 0.25 }}
                                >
                                  Address Warning
                                </AlertTitle>
                                {shipmentAddress.addressDeficiencies?.length === 1 && (
                                  <Box
                                    component='ul'
                                    role='list'
                                    aria-label='Address issues'
                                    sx={{
                                      m: 0,
                                      pl: 2,
                                      listStyleType: 'disc',
                                      '& li': { py: 0.25 },
                                    }}
                                  >
                                    <li>
                                      <Typography variant='body2'>
                                        {formatDeficiency(shipmentAddress.addressDeficiencies[0])}
                                      </Typography>
                                    </li>
                                  </Box>
                                )}
                                {shipmentAddress.addressDeficiencies?.length > 1 && (
                                  <>
                                    <Chip
                                      label={`${shipmentAddress.addressDeficiencies.length} issues`}
                                      size='small'
                                      variant='outlined'
                                      color='warning'
                                      onClick={() => setIsDeficiencyExpanded((prev) => !prev)}
                                      onDelete={() => setIsDeficiencyExpanded((prev) => !prev)}
                                      deleteIcon={
                                        isDeficiencyExpanded ? (
                                          <ArrowDropUpIcon />
                                        ) : (
                                          <ArrowDropDownIcon />
                                        )
                                      }
                                      sx={{ cursor: 'pointer', height: 24, mt: 0.5 }}
                                      aria-expanded={isDeficiencyExpanded}
                                      aria-label={`${shipmentAddress.addressDeficiencies.length} address issues, click to ${isDeficiencyExpanded ? 'collapse' : 'expand'}`}
                                    />
                                    <Collapse in={isDeficiencyExpanded} timeout='auto'>
                                      <Box
                                        component='ul'
                                        role='list'
                                        aria-label='Address issues'
                                        sx={{
                                          mt: 0.5,
                                          mb: 0,
                                          pl: 2,
                                          listStyleType: 'disc',
                                          '& li': { py: 0.25 },
                                        }}
                                      >
                                        {shipmentAddress.addressDeficiencies.map((d) => (
                                          <li key={d}>
                                            <Typography variant='body2'>
                                              {formatDeficiency(d)}
                                            </Typography>
                                          </li>
                                        ))}
                                      </Box>
                                    </Collapse>
                                  </>
                                )}
                              </Alert>
                            )}

                            {/* INVALID or BLOCKED — attention-capturing error alert box */}
                            {!isValidatingAddress &&
                              ['INVALID', 'BLOCKED'].includes(shipmentAddress.addressValidity) && (
                                <Alert
                                  severity='error'
                                  icon={<ErrorIcon fontSize='inherit' />}
                                  role='alert'
                                  sx={{
                                    mt: 1.5,
                                    borderRadius: 1.5,
                                    '& .MuiAlert-message': { width: '100%' },
                                  }}
                                >
                                  <AlertTitle
                                    sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 0.25 }}
                                  >
                                    Invalid Address
                                  </AlertTitle>
                                  {shipmentAddress.addressDeficiencies?.length === 1 && (
                                    <Box
                                      component='ul'
                                      role='list'
                                      aria-label='Address issues'
                                      sx={{
                                        m: 0,
                                        pl: 2,
                                        listStyleType: 'disc',
                                        '& li': { py: 0.25 },
                                      }}
                                    >
                                      <li>
                                        <Typography variant='body2'>
                                          {formatDeficiency(shipmentAddress.addressDeficiencies[0])}
                                        </Typography>
                                      </li>
                                    </Box>
                                  )}
                                  {shipmentAddress.addressDeficiencies?.length > 1 && (
                                    <>
                                      <Chip
                                        label={`${shipmentAddress.addressDeficiencies.length} issues`}
                                        size='small'
                                        variant='outlined'
                                        color='error'
                                        onClick={() => setIsDeficiencyExpanded((prev) => !prev)}
                                        onDelete={() => setIsDeficiencyExpanded((prev) => !prev)}
                                        deleteIcon={
                                          isDeficiencyExpanded ? (
                                            <ArrowDropUpIcon />
                                          ) : (
                                            <ArrowDropDownIcon />
                                          )
                                        }
                                        sx={{ cursor: 'pointer', height: 24, mt: 0.5 }}
                                        aria-expanded={isDeficiencyExpanded}
                                        aria-label={`${shipmentAddress.addressDeficiencies.length} address issues, click to ${isDeficiencyExpanded ? 'collapse' : 'expand'}`}
                                      />
                                      <Collapse in={isDeficiencyExpanded} timeout='auto'>
                                        <Box
                                          component='ul'
                                          role='list'
                                          aria-label='Address issues'
                                          sx={{
                                            mt: 0.5,
                                            mb: 0,
                                            pl: 2,
                                            listStyleType: 'disc',
                                            '& li': { py: 0.25 },
                                          }}
                                        >
                                          {shipmentAddress.addressDeficiencies.map((d) => (
                                            <li key={d}>
                                              <Typography variant='body2'>
                                                {formatDeficiency(d)}
                                              </Typography>
                                            </li>
                                          ))}
                                        </Box>
                                      </Collapse>
                                    </>
                                  )}
                                </Alert>
                              )}

                            {/* Legacy fallback: addressValidity not set, isVerified=true */}
                            {!isValidatingAddress &&
                              !shipmentAddress.addressValidity &&
                              shipmentAddress.isVerified === true && (
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}
                                >
                                  <CheckCircleIcon
                                    sx={{ color: 'success.main', fontSize: '1.1rem' }}
                                  />
                                  <Typography variant='body2' color='success.main' fontWeight={500}>
                                    Address Verified
                                  </Typography>
                                </Box>
                              )}

                            {/* Legacy fallback: addressValidity not set, not verified */}
                            {!isValidatingAddress &&
                              !shipmentAddress.addressValidity &&
                              shipmentAddress.isVerified !== true && (
                                <Alert
                                  severity='error'
                                  icon={<WarningAmberIcon fontSize='inherit' />}
                                  role='alert'
                                  sx={{ mt: 1.5, borderRadius: 1.5 }}
                                >
                                  Could not verify address
                                </Alert>
                              )}
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                  {/* 30% - Partner and Practice */}
                  <Box
                    sx={{
                      flex: '0 0 30%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                      justifyContent: 'flex-start',
                      pt: 8,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <BusinessIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
                      <Typography variant='body2' color='text.secondary' sx={{ mr: 0.5 }}>
                        Partner:
                      </Typography>
                      <Typography variant='body1' fontWeight={600}>
                        {partnerName ? casingFormat(partnerName) : '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <BusinessIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
                      <Typography variant='body2' color='text.secondary' sx={{ mr: 0.5 }}>
                        Practice:
                      </Typography>
                      <Typography variant='body1' fontWeight={600}>
                        {practiceName ? casingFormat(practiceName) : '-'}
                      </Typography>
                    </Box>
                    {tags && tags.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <Typography variant='body2' color='text.secondary'>
                          Tags:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {tags.map((tag) => (
                            <Tooltip
                              key={tag.ruleId}
                              title={tag.tooltip || ''}
                              arrow
                              placement='top'
                            >
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  bgcolor: tag.color ? hex2rgba(tag.color, 0.18) : 'grey.100',
                                  border: '1px solid',
                                  borderColor: tag.color ? hex2rgba(tag.color, 0.45) : 'grey.400',
                                  borderRadius: '999px',
                                  px: 1.125,
                                  py: 0.4,
                                  cursor: 'default',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 7,
                                    height: 7,
                                    borderRadius: '50%',
                                    bgcolor: tag.color || 'grey.500',
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant='caption'
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '0.78rem',
                                    lineHeight: 1.2,
                                    whiteSpace: 'nowrap',
                                    color: tag.color || 'text.primary',
                                  }}
                                >
                                  {tag.label}
                                </Typography>
                              </Box>
                            </Tooltip>
                          ))}
                        </Box>
                      </Box>
                    )}
                    {deliveryInstructions && (
                      <Box
                        sx={{
                          mt: 2,
                          ml: 2,
                          p: 1.25,
                          bgcolor: 'warning.50',
                          border: '1px solid',
                          borderColor: 'warning.200',
                          borderRadius: 1,
                          borderLeft: '3px solid',
                          borderLeftColor: 'warning.main',
                        }}
                      >
                        <Typography
                          variant='caption'
                          fontWeight={700}
                          color='warning.dark'
                          sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                        >
                          Delivery Instructions
                        </Typography>
                        <Typography
                          variant='body2'
                          fontWeight={500}
                          sx={{ mt: 0.25, wordBreak: 'break-word' }}
                        >
                          {deliveryInstructions}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {/* 30% - Shipping Service (hidden for shipped orders) */}
                  {!isShipped && (
                    <Box
                      sx={{
                        flex: '0 0 30%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 1.5,
                      }}
                    >
                      <FormControl sx={{ minWidth: 180, maxWidth: 200 }} disabled={isCreatingLabel}>
                        <InputLabel id='shipping-service-label' shrink>
                          Shipping Service
                        </InputLabel>
                        <Select
                          labelId='shipping-service-label'
                          value={shippingService}
                          onChange={(e) => setShippingService(e.target.value)}
                          label='Shipping Service'
                          size='small'
                        >
                          <MenuItem value='FedEx'>FedEx</MenuItem>
                          {!isManualShipment && (
                            <MenuItem value='Shipstation'>Shipstation</MenuItem>
                          )}
                          {!isManualShipment && <MenuItem value='Manual'>Manual</MenuItem>}
                        </Select>
                      </FormControl>
                      {/* Send to ShipStation Button */}
                      {shippingService === 'Shipstation' && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            alignItems: 'flex-end',
                            width: '100%',
                          }}
                        >
                          <Button
                            variant='outlined'
                            sx={{ textTransform: 'none' }}
                            onClick={handleOpenSendToShipStationDialog}
                            disabled={isSendingToShipStation}
                          >
                            Send to ShipStation
                          </Button>
                          <Typography
                            variant='caption'
                            sx={{ color: 'error.main', textAlign: 'right' }}
                          >
                            Please Manage in Externally
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Card>

              {/* Shipment Items Card */}
              <Card
                sx={{
                  boxShadow: theme.shadows[2],
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: 'background.paper',
                }}
              >
                <CardContent sx={{ pl: 3.5 }}>
                  {allItemsMoved && (
                    <Alert severity='info' sx={{ mb: 2 }}>
                      This shipment is empty because all items were moved. Add items to create a
                      label.
                    </Alert>
                  )}
                  <Box
                    onClick={() => setIsShipmentItemsExpanded(!isShipmentItemsExpanded)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <KeyboardArrowDownIcon
                        sx={{
                          color: 'primary.main',
                          transform: isShipmentItemsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                          transition: 'transform 0.3s ease',
                        }}
                      />
                      <Typography variant='h6' fontWeight={600}>
                        Shipment Items
                      </Typography>
                      {shipmentItems && shipmentItems.length > 0 && (
                        <Typography variant='body2' color='text.secondary'>
                          {shipmentItems.length} {shipmentItems.length === 1 ? 'Item' : 'Items'}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {shipmentStatus === 'REQUEST_CREATED' && !isShipped && (
                        <Button
                          size='small'
                          variant='text'
                          startIcon={<EditIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setRemoveItemsOpen(true);
                          }}
                        >
                          Edit
                        </Button>
                      )}
                      {shipmentStatus === 'REQUEST_CREATED' && !isShipped && (
                        <Button
                          size='small'
                          variant='outlined'
                          startIcon={<SwapHorizIcon />}
                          disabled={allItemsMoved}
                          onClick={(e) => {
                            e.stopPropagation();
                            setManageItemsOpen(true);
                          }}
                        >
                          Manage Items
                        </Button>
                      )}
                    </Box>
                  </Box>
                  <Collapse in={isShipmentItemsExpanded}>
                    <Box sx={{ mt: 2.5 }}>
                      <TableContainer
                        sx={{
                          borderRadius: 1,
                          border: `1px solid ${theme.palette.divider}`,
                          bgcolor: 'background.paper',
                          maxHeight: 300,
                          overflow: 'auto',
                        }}
                      >
                        <Table size='small'>
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  bgcolor: 'grey.100',
                                  textTransform: 'uppercase',
                                  color: 'text.secondary',
                                }}
                              >
                                Order Id
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  bgcolor: 'grey.100',
                                  textTransform: 'uppercase',
                                  color: 'text.secondary',
                                }}
                              >
                                Drug Name
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 600,
                                  bgcolor: 'grey.100',
                                  textTransform: 'uppercase',
                                  color: 'text.secondary',
                                }}
                              >
                                NDC
                              </TableCell>
                              <TableCell
                                align='center'
                                sx={{
                                  fontWeight: 600,
                                  bgcolor: 'grey.100',
                                  textTransform: 'uppercase',
                                  color: 'text.secondary',
                                }}
                              >
                                Quantity
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {shipmentItems && shipmentItems.length > 0 ? (
                              map(shipmentItems, (item) => (
                                <TableRow key={item.lineItemId}>
                                  <TableCell sx={{ fontFamily: 'monospace' }}>
                                    {item.orderId || referenceId}
                                  </TableCell>
                                  <TableCell>{item.name}</TableCell>
                                  <TableCell sx={{ fontFamily: 'monospace' }}>
                                    {item.sku || '-'}
                                  </TableCell>
                                  <TableCell align='center'>{item.quantity}</TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={4} align='center' sx={{ py: 4 }}>
                                  <Typography variant='body2' color='text.secondary'>
                                    No items found
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>

              {shipment?.notes && shipment.notes.length > 0 && (
                <Card sx={{ mt: 2 }}>
                  <CardContent sx={{ p: 3.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <StickyNote2Icon sx={{ color: 'primary.main' }} />
                      <Typography variant='h6' fontWeight={600}>
                        Notes
                      </Typography>
                    </Box>

                    {map(
                      [...shipment.notes].sort((a, b) => {
                        const aTime = new Date(a.noteCreatedAt).getTime() || 0;
                        const bTime = new Date(b.noteCreatedAt).getTime() || 0;
                        return aTime - bTime;
                      }),
                      (note, idx, arr) => {
                        const isLast = idx === arr.length - 1;
                        const author = note.noteAuthor || {};
                        const fullName =
                          `${author.firstName ?? ''} ${author.lastName ?? ''}`.trim();
                        const noteDate = note.noteCreatedAt ? new Date(note.noteCreatedAt) : null;
                        const orderRefId = note.noteContextId || null;
                        return (
                          <Box
                            key={note.noteId}
                            sx={{
                              display: 'flex',
                              alignItems: 'stretch',
                              px: 1,
                              py: 1.5,
                              borderBottom: '1px solid',
                              borderColor: theme.palette.divider,
                            }}
                          >
                            <Box
                              sx={{
                                width: 110,
                                flexShrink: 0,
                                textAlign: 'right',
                                pr: 1.5,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                                pt: 0.25,
                              }}
                            >
                              {noteDate && (
                                <>
                                  <Typography
                                    variant='body2'
                                    color='text.secondary'
                                    sx={{ display: 'block', lineHeight: 1.5, fontSize: '0.7rem' }}
                                  >
                                    {format(noteDate, 'MM/dd/yyyy')}
                                  </Typography>
                                  <Typography
                                    variant='body2'
                                    color='primary.main'
                                    fontWeight={700}
                                    sx={{ display: 'block', lineHeight: 1.5, fontSize: '0.7rem' }}
                                  >
                                    {format(noteDate, 'hh:mm:ss a')}
                                  </Typography>
                                </>
                              )}
                              <Typography
                                variant='caption'
                                color='text.secondary'
                                sx={{
                                  display: 'block',
                                  lineHeight: 1.4,
                                  fontStyle: 'italic',
                                  fontSize: '0.65rem',
                                }}
                              >
                                {fullName || 'Unknown'}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                flexShrink: 0,
                                width: 20,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  bgcolor: 'primary.main',
                                  flexShrink: 0,
                                  mt: 0.4,
                                }}
                              />
                              {!isLast && (
                                <Box
                                  sx={{
                                    width: 2,
                                    flex: 1,
                                    bgcolor: 'grey.300',
                                    mt: 0.5,
                                    minHeight: 12,
                                  }}
                                />
                              )}
                            </Box>

                            {/* Right — Order # + chip + content */}
                            <Box sx={{ flex: 1, minWidth: 0, pl: 1.5 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  flexWrap: 'wrap',
                                  mb: 0.75,
                                }}
                              >
                                {orderRefId && (
                                  <Typography
                                    variant='subtitle2'
                                    color='primary.main'
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: '0.85rem',
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    Order #{orderRefId}
                                  </Typography>
                                )}
                                <Chip
                                  label='Shipping Note'
                                  size='small'
                                  color='info'
                                  sx={{ height: 16, fontSize: '0.65rem' }}
                                />
                              </Box>
                              <Box sx={{ wordBreak: 'break-word' }}>
                                <RichTextViewer
                                  value={note.noteContent}
                                  id={`ShipmentViewNote-${note.noteId}`}
                                />
                              </Box>
                            </Box>
                          </Box>
                        );
                      }
                    )}
                  </CardContent>
                </Card>
              )}

              {pdfBlobUrl && (
                <Card sx={{ mt: 2 }}>
                  <CardContent>
                    <Grid container sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <Grid item>
                        <Typography variant='h6' component='span'>
                          Shipping Label
                        </Typography>
                      </Grid>

                      <Grid item>
                        <Button
                          variant='outlined'
                          onClick={openPdfUrl}
                          sx={{ width: 100, mr: 1, zIndex: 10 }}
                        >
                          Download
                        </Button>
                        <Button
                          variant='contained'
                          onClick={handlePrintLabel}
                          sx={{ width: 100, zIndex: 10 }}
                        >
                          Print
                        </Button>
                      </Grid>
                    </Grid>

                    <Box sx={{ ml: 5, zIndex: -1 }}>
                      <Document file={pdfBlobUrl} onLoadSuccess={onDocumentLoadSuccess}>
                        {Array.from(new Array(numPages), (_, index) => (
                          <Page key={`page_${index + 1}`} pageNumber={index + 1} />
                        ))}
                      </Document>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Grid>

            {/* Right Panel - Shipment Details (for shipped) or Configure Shipment (for ready to ship) */}
            {shippingService !== 'Shipstation' && shippingService !== 'Manual' && (
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    position: 'sticky',
                    top: 20,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    pb: 4,
                  }}
                >
                  {isShipped ? (
                    <>
                      {/* Blue Card - Shipping Option, Ship Date, Cost */}
                      <Card
                        sx={{
                          bgcolor: 'primary.light',
                          color: 'white',
                          borderRadius: 2,
                          boxShadow: theme.shadows[3],
                        }}
                      >
                        <CardContent sx={{ p: 2.5 }}>
                          {/* Header Row with Shipping Option and Menu */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              mb: 1.5,
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant='h5'
                                fontWeight={700}
                                sx={{ color: 'white', mb: 1 }}
                              >
                                {getShippingOption(service || serviceCode, true)}
                              </Typography>
                              {hasOriginalServiceCode && (
                                <Typography
                                  variant='body2'
                                  sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5 }}
                                >
                                  Original:{' '}
                                  {shippingDisplayOverrides[originalServiceCode] ||
                                    originalServiceCode}
                                </Typography>
                              )}
                              {/* Tracking Number below Shipping Option */}
                              {(trackingNumber || updatedTrackingNumber) && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                  }}
                                >
                                  <Typography
                                    variant='body1'
                                    sx={{
                                      fontFamily: 'monospace',
                                      color: 'white',
                                      fontWeight: 500,
                                      fontSize: '1.1rem',
                                    }}
                                  >
                                    {trackingNumber || updatedTrackingNumber}
                                  </Typography>
                                  <IconButton
                                    size='small'
                                    onClick={() => {
                                      const trackingNum = trackingNumber || updatedTrackingNumber;
                                      if (trackingNum) {
                                        navigator.clipboard.writeText(trackingNum);
                                      }
                                    }}
                                    sx={{
                                      p: 0.5,
                                      color: 'white',
                                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' },
                                    }}
                                    title='Copy tracking number'
                                  >
                                    <ContentCopyIcon fontSize='small' />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                            <IconButton
                              size='small'
                              onClick={(e) => setLabelMenuAnchor(e.currentTarget)}
                              sx={{
                                color: 'white',
                                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' },
                              }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                            <Menu
                              anchorEl={labelMenuAnchor}
                              open={Boolean(labelMenuAnchor)}
                              onClose={() => setLabelMenuAnchor(null)}
                              anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                              }}
                              transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                              }}
                            >
                              <Tooltip title={voidLabelTooltip} arrow placement='left'>
                                <span>
                                  <MenuItem
                                    onClick={() => {
                                      setLabelMenuAnchor(null);
                                      setVoidLabelDialogOpen(true);
                                    }}
                                    disabled={isVoidLabelDisabled}
                                  >
                                    Void Label
                                  </MenuItem>
                                </span>
                              </Tooltip>
                            </Menu>
                          </Box>

                          {/* Ship Date and Cost Row */}
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 4,
                              pt: 2,
                              borderTop: '1px solid rgba(255, 255, 255, 0.2)',
                            }}
                          >
                            <Box>
                              <Typography
                                variant='caption'
                                sx={{
                                  display: 'block',
                                  mb: 0.5,
                                  color: 'rgba(255, 255, 255, 0.8)',
                                }}
                              >
                                Label Created Date
                              </Typography>
                              <Typography variant='body2' fontWeight={600} sx={{ color: 'white' }}>
                                {displayShippedAt
                                  ? format(new Date(displayShippedAt), 'MM/dd/yyyy')
                                  : '—'}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant='caption'
                                sx={{
                                  display: 'block',
                                  mb: 0.5,
                                  color: 'rgba(255, 255, 255, 0.8)',
                                }}
                              >
                                Ship Date
                              </Typography>
                              <Typography variant='body2' fontWeight={600} sx={{ color: 'white' }}>
                                {formatShipDate(shipDate)}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                variant='caption'
                                sx={{
                                  display: 'block',
                                  mb: 0.5,
                                  color: 'rgba(255, 255, 255, 0.8)',
                                }}
                              >
                                Cost
                              </Typography>
                              <Typography
                                variant='body2'
                                fontWeight={600}
                                sx={{
                                  color: 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                $
                                {isUpdatingShipment ? (
                                  <CircularProgress size={16} sx={{ color: 'white' }} />
                                ) : (
                                  getDisplayCost()
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>

                      {/* Shipment Record Card */}
                      <Card
                        sx={{
                          boxShadow: theme.shadows[3],
                          borderRadius: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          bgcolor: 'grey.50',
                        }}
                      >
                        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              py: 1.5,
                              px: 1.5,
                              '&:hover': { bgcolor: 'grey.100' },
                            }}
                          >
                            <Box
                              onClick={() => setIsConfigExpanded(!isConfigExpanded)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                cursor: 'pointer',
                                flex: 1,
                              }}
                            >
                              <KeyboardArrowDownIcon
                                sx={{
                                  color: 'text.secondary',
                                  transform: isConfigExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                                  transition: 'transform 0.3s ease',
                                }}
                              />
                              <Typography variant='body1' fontWeight={500}>
                                Shipment Record
                              </Typography>
                            </Box>
                          </Box>

                          <Collapse in={isConfigExpanded}>
                            <TableContainer sx={{ p: 2 }}>
                              <Table size='small' sx={{ bgcolor: 'grey.50' }}>
                                <TableBody>
                                  <TableRow>
                                    <TableCell
                                      sx={{
                                        borderBottom: 'none',
                                        py: 1,
                                        px: 1.5,
                                        width: '40%',
                                        color: 'text.secondary',
                                        fontSize: '0.75rem',
                                      }}
                                    >
                                      Status
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        borderBottom: 'none',
                                        py: 1,
                                        px: 1.5,
                                        fontWeight: 500,
                                      }}
                                    >
                                      {isShipped ? 'Shipped' : 'Ready to Ship'}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell
                                      sx={{
                                        borderBottom: 'none',
                                        py: 1,
                                        px: 1.5,
                                        color: 'text.secondary',
                                        fontSize: '0.75rem',
                                      }}
                                    >
                                      Ship From
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        borderBottom: 'none',
                                        py: 1,
                                        px: 1.5,
                                        fontWeight: 500,
                                      }}
                                    >
                                      {getFulfillingPharmacyName()}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell
                                      sx={{
                                        borderBottom: 'none',
                                        py: 1,
                                        px: 1.5,
                                        color: 'text.secondary',
                                        fontSize: '0.75rem',
                                      }}
                                    >
                                      Weight
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        borderBottom: 'none',
                                        py: 1,
                                        px: 1.5,
                                        fontWeight: 500,
                                      }}
                                    >
                                      {(() => {
                                        const lbs = parseFloat(weightLbs) || 0;
                                        const oz = parseFloat(weightOz) || 0;
                                        if (lbs === 0 && oz === 0) return '—';
                                        if (lbs > 0 && oz > 0) return `${lbs} lb ${oz} oz`;
                                        if (lbs > 0) return `${lbs} lb`;
                                        if (oz > 0) return `${oz} oz`;
                                        return '—';
                                      })()}
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell
                                      sx={{
                                        borderBottom: 'none',
                                        py: 1,
                                        px: 1.5,
                                        color: 'text.secondary',
                                        fontSize: '0.75rem',
                                      }}
                                    >
                                      Package
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        borderBottom: 'none',
                                        py: 1,
                                        px: 1.5,
                                        fontWeight: 500,
                                      }}
                                    >
                                      {packageCode ? getShippingOption(packageCode, true) : '—'}
                                    </TableCell>
                                  </TableRow>
                                  {dimensions && (
                                    <TableRow>
                                      <TableCell
                                        sx={{
                                          borderBottom: 'none',
                                          py: 1,
                                          px: 1.5,
                                          color: 'text.secondary',
                                          fontSize: '0.75rem',
                                        }}
                                      >
                                        Dimensions
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          borderBottom: 'none',
                                          py: 1,
                                          px: 1.5,
                                          fontWeight: 500,
                                        }}
                                      >
                                        {(() => {
                                          const opt = dimensionOptions.find(
                                            (d) =>
                                              `${d.length}x${d.width}x${d.height}` === dimensions
                                          );
                                          return opt ? opt.label : dimensions;
                                        })()}
                                      </TableCell>
                                    </TableRow>
                                  )}
                                  <TableRow>
                                    <TableCell
                                      sx={{
                                        borderBottom: 'none',
                                        py: 1,
                                        px: 1.5,
                                        color: 'text.secondary',
                                        fontSize: '0.75rem',
                                      }}
                                    >
                                      Insurance
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        borderBottom: 'none',
                                        py: 1,
                                        px: 1.5,
                                        fontWeight: 500,
                                      }}
                                    >
                                      ${getDisplayInsuranceAmount()}
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Collapse>
                        </CardContent>
                      </Card>
                    </>
                  ) : (
                    <Card
                      sx={{
                        boxShadow: theme.shadows[3],
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <CardContent sx={{ p: 3, pb: 4 }}>
                        <Box
                          onClick={() => setIsConfigExpanded(!isConfigExpanded)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.8 },
                          }}
                        >
                          <KeyboardArrowDownIcon
                            sx={{
                              color: 'primary.main',
                              transform: isConfigExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                              transition: 'transform 0.3s ease',
                            }}
                          />
                          <Typography variant='h6' fontWeight={600} color='primary.main'>
                            Configure Shipment
                          </Typography>
                        </Box>

                        <Collapse in={isConfigExpanded}>
                          <Box sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <FormControl fullWidth>
                                  <InputLabel id='ship-from-label' shrink>
                                    Ship From
                                  </InputLabel>
                                  <Select
                                    disabled
                                    labelId='ship-from-label'
                                    value={shipFrom}
                                    onChange={(e) => setShipFrom(e.target.value)}
                                    label='Ship From'
                                    renderValue={(selected) => {
                                      const selectedPharmacy = find(
                                        fulfillingPharmacies,
                                        (pharmacy) => pharmacy.fulfillingPharmacyId === selected
                                      );

                                      if (!selectedPharmacy) return '';
                                      const facilityState = find(
                                        facilities,
                                        (facility) =>
                                          facility.fulfillingPharmacyId ===
                                          selectedPharmacy.fulfillingPharmacyId
                                      )?.state;

                                      return facilityState
                                        ? `${selectedPharmacy.fulfillingPharmacyName} - ${facilityState}`
                                        : selectedPharmacy.fulfillingPharmacyName;
                                    }}
                                  >
                                    {map(
                                      fulfillingPharmacies,
                                      ({
                                        fulfillingPharmacyId: pharmacyId,
                                        fulfillingPharmacyName,
                                      }) => {
                                        const facilityState = find(
                                          facilities,
                                          (facility) => facility.fulfillingPharmacyId === pharmacyId
                                        )?.state;

                                        return (
                                          <MenuItem key={pharmacyId} value={pharmacyId}>
                                            {facilityState
                                              ? `${fulfillingPharmacyName} - ${facilityState}`
                                              : fulfillingPharmacyName}
                                          </MenuItem>
                                        );
                                      }
                                    )}
                                  </Select>
                                </FormControl>
                              </Grid>

                              <Grid item xs={12} sx={{ mt: 1 }}>
                                <Typography variant='body2' fontWeight={500} sx={{ mb: 1 }}>
                                  Weight
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                  <TextField
                                    type='number'
                                    value={weightLbs}
                                    onChange={(e) => setWeightLbs(e.target.value)}
                                    size='small'
                                    disabled={isCreatingLabel}
                                    validation={[validateRequired('Required')]}
                                    sx={{
                                      flex: 1,
                                      '& .MuiInputBase-input': {
                                        textAlign: 'right',
                                        fontWeight: 200,
                                      },
                                    }}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position='start'>lbs</InputAdornment>
                                      ),
                                      endAdornment: (
                                        <InputAdornment position='end' sx={{ mr: 0 }}>
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              alignItems: 'flex-end',
                                            }}
                                          >
                                            <IconButton
                                              size='small'
                                              onClick={handleIncrementLbs}
                                              disabled={isCreatingLabel}
                                              sx={{
                                                p: 0,
                                                height: '14px',
                                                width: '20px',
                                                minWidth: '20px',
                                              }}
                                            >
                                              <ArrowDropUpIcon sx={{ fontSize: '20px' }} />
                                            </IconButton>
                                            <IconButton
                                              size='small'
                                              onClick={handleDecrementLbs}
                                              disabled={
                                                isCreatingLabel || parseFloat(weightLbs) <= 0
                                              }
                                              sx={{
                                                p: 0,
                                                height: '14px',
                                                width: '20px',
                                                minWidth: '20px',
                                              }}
                                            >
                                              <ArrowDropDownIcon sx={{ fontSize: '20px' }} />
                                            </IconButton>
                                          </Box>
                                        </InputAdornment>
                                      ),
                                      inputProps: { min: 0 },
                                    }}
                                  />
                                  <TextField
                                    type='number'
                                    value={weightOz}
                                    onChange={(e) => setWeightOz(e.target.value)}
                                    size='small'
                                    disabled={isCreatingLabel}
                                    sx={{
                                      flex: 1,
                                      '& .MuiInputBase-input': {
                                        textAlign: 'right',
                                        fontWeight: 200,
                                      },
                                    }}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position='start'>oz</InputAdornment>
                                      ),
                                      endAdornment: (
                                        <InputAdornment position='end' sx={{ mr: 0 }}>
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              alignItems: 'flex-end',
                                            }}
                                          >
                                            <IconButton
                                              size='small'
                                              onClick={handleIncrementOz}
                                              disabled={
                                                isCreatingLabel || parseFloat(weightOz) > 15
                                              }
                                              sx={{
                                                p: 0,
                                                height: '14px',
                                                width: '20px',
                                                minWidth: '20px',
                                              }}
                                            >
                                              <ArrowDropUpIcon sx={{ fontSize: '20px' }} />
                                            </IconButton>
                                            <IconButton
                                              size='small'
                                              onClick={handleDecrementOz}
                                              disabled={
                                                isCreatingLabel ||
                                                (parseFloat(weightOz) <= 0 &&
                                                  parseFloat(weightLbs) <= 0)
                                              }
                                              sx={{
                                                p: 0,
                                                height: '14px',
                                                width: '20px',
                                                minWidth: '20px',
                                              }}
                                            >
                                              <ArrowDropDownIcon sx={{ fontSize: '20px' }} />
                                            </IconButton>
                                          </Box>
                                        </InputAdornment>
                                      ),
                                      inputProps: { min: 0, max: 15 },
                                    }}
                                  />
                                </Box>
                              </Grid>

                              {!isValidWeight && (
                                <Grid item xs={12}>
                                  <Alert severity='error' sx={{ mt: 1 }}>
                                    {isWeightOver50
                                      ? 'Shipment weight cannot exceed 50 LB'
                                      : 'Shipment Weight is invalid'}
                                  </Alert>
                                </Grid>
                              )}

                              <Grid item xs={12} sx={{ mt: 1 }}>
                                <Form
                                  key={shipment?.shipmentId}
                                  initialValues={{
                                    shipDate: formInitialShipDate,
                                  }}
                                  onSubmit={() => {}}
                                  render={({ form }) => (
                                    <>
                                      <ShipDateFormSyncer setShipDate={setShipDate} />
                                      <TriggerShipDateValidation form={form} />
                                      <DateField
                                        name='shipDate'
                                        id='ShipmentViewDialog-shipDate'
                                        label='Ship date'
                                        minDate={startOfDay(subDays(new Date(), 1))}
                                        maxDate={endOfDay(addDays(new Date(), 7))}
                                        disabled={isCreatingLabel}
                                        disablePast
                                        validations={[
                                          validateDateNotInPast(
                                            'Date cannot be set to a past date.'
                                          ),
                                        ]}
                                      />
                                    </>
                                  )}
                                />
                              </Grid>

                              <Grid item xs={12} sx={{ mt: 1 }}>
                                <FormControl fullWidth disabled={isCreatingLabel}>
                                  <InputLabel id='service-label' shrink>
                                    Service
                                  </InputLabel>
                                  <Select
                                    labelId='service-label'
                                    value={service}
                                    onChange={(e) => {
                                      const selectedService = e.target.value;
                                      setService(selectedService);
                                      const config = servicePackageConfig[selectedService];
                                      if (config) {
                                        setPackageCode(config.packages[0]);
                                      }
                                      // Reset dimensions since default package is never YOUR_PACKAGING
                                      setDimensions('');
                                    }}
                                    label='Service'
                                    displayEmpty
                                    renderValue={(selected) =>
                                      selected ? getShippingOption(selected, true) : ''
                                    }
                                  >
                                    <MenuItem value='FEDEX_STANDARD_OVERNIGHT'>
                                      {getShippingOption('FEDEX_STANDARD_OVERNIGHT', true)}
                                    </MenuItem>
                                    <MenuItem value='FEDEX_PRIORITY_OVERNIGHT'>
                                      {getShippingOption('FEDEX_PRIORITY_OVERNIGHT', true)}
                                    </MenuItem>
                                    <MenuItem value='FEDEX_2DAY'>
                                      {getShippingOption('FEDEX_2DAY', true)}
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                                {hasOriginalServiceCode && (
                                  <Typography
                                    variant='body2'
                                    color='text.secondary'
                                    sx={{ mt: 0.5 }}
                                  >
                                    Original:{' '}
                                    {shippingDisplayOverrides[originalServiceCode] ||
                                      originalServiceCode}
                                  </Typography>
                                )}
                              </Grid>

                              <Grid item xs={12} sx={{ mt: 1 }}>
                                <FormControl fullWidth disabled={isCreatingLabel || !service}>
                                  <InputLabel id='package-label' shrink>
                                    Package
                                  </InputLabel>
                                  <Select
                                    labelId='package-label'
                                    value={packageCode}
                                    onChange={(e) => {
                                      const selectedPackage = e.target.value;
                                      setPackageCode(selectedPackage);
                                      if (selectedPackage !== 'YOUR_PACKAGING') {
                                        setDimensions('');
                                      }
                                    }}
                                    label='Package'
                                    displayEmpty
                                    renderValue={(selected) =>
                                      selected ? getShippingOption(selected, true) : ''
                                    }
                                  >
                                    {service &&
                                      servicePackageConfig[service] &&
                                      servicePackageConfig[service].packages.map((pkg) => (
                                        <MenuItem key={pkg} value={pkg}>
                                          {getShippingOption(pkg, true)}
                                        </MenuItem>
                                      ))}
                                  </Select>
                                </FormControl>
                              </Grid>

                              {packageCode === 'YOUR_PACKAGING' && (
                                <Grid item xs={12} sx={{ mt: 1 }}>
                                  <FormControl
                                    fullWidth
                                    disabled={isCreatingLabel}
                                    error={packageCode === 'YOUR_PACKAGING' && !dimensions}
                                  >
                                    <InputLabel id='dimensions-label' shrink>
                                      Dimensions
                                    </InputLabel>
                                    <Select
                                      labelId='dimensions-label'
                                      value={dimensions}
                                      onChange={(e) => setDimensions(e.target.value)}
                                      label='Dimensions'
                                      notched
                                      displayEmpty
                                      renderValue={(selected) => {
                                        if (!selected) return '';
                                        const opt = dimensionOptions.find(
                                          (d) => `${d.length}x${d.width}x${d.height}` === selected
                                        );
                                        return opt ? opt.label : selected;
                                      }}
                                    >
                                      {dimensionOptions.map((opt) => {
                                        const key = `${opt.length}x${opt.width}x${opt.height}`;
                                        return (
                                          <MenuItem key={key} value={key}>
                                            {opt.label}
                                          </MenuItem>
                                        );
                                      })}
                                    </Select>
                                    {packageCode === 'YOUR_PACKAGING' && !dimensions && (
                                      <FormHelperText>
                                        Dimensions are required for the selected package
                                      </FormHelperText>
                                    )}
                                  </FormControl>
                                </Grid>
                              )}

                              <Grid item xs={12} sx={{ mt: 0.5 }}>
                                <FormControlLabel
                                  disabled={isCreatingLabel}
                                  control={
                                    <Checkbox
                                      checked={Boolean(enableSaturdayDelivery)}
                                      onChange={(e) => setEnableSaturdayDelivery(e.target.checked)}
                                      name='enableSaturdayDelivery'
                                    />
                                  }
                                  label='Saturday shipping'
                                />
                                <FormControlLabel
                                  disabled={isCreatingLabel}
                                  control={
                                    <Checkbox
                                      checked={Boolean(signatureRequired)}
                                      onChange={(e) => setSignatureRequired(e.target.checked)}
                                      name='signatureRequired'
                                    />
                                  }
                                  label='Signature Required'
                                />
                              </Grid>

                              <Grid item xs={12} sx={{ mt: 1 }}>
                                <FormControl fullWidth disabled={isCreatingLabel}>
                                  <InputLabel id='insurance-label' shrink>
                                    Insurance
                                  </InputLabel>
                                  <Select
                                    labelId='insurance-label'
                                    label='Insurance'
                                    value={hasInsurance ? 'ADD' : 'NONE'}
                                    onChange={({ target: { value } }) => {
                                      setHasInsurance(value === 'ADD');
                                    }}
                                  >
                                    <MenuItem value='NONE'>None</MenuItem>
                                    <MenuItem value='ADD'>Insurance</MenuItem>
                                  </Select>
                                </FormControl>

                                {enableInsuranceRecommended && (
                                  <Alert
                                    severity='warning'
                                    icon={
                                      <WarningAmberIcon
                                        fontSize='inherit'
                                        sx={{ color: perfectRxTheme.palette.warning.warningColour }}
                                      />
                                    }
                                    role='alert'
                                    sx={{
                                      mt: 1.5,
                                      borderRadius: 1.5,
                                      bgcolor: `${perfectRxTheme.palette.warning.warningColour}15`,
                                      color: theme.palette.text.primary,
                                      '& .MuiAlert-message': { width: '100%' },
                                    }}
                                  >
                                    <AlertTitle
                                      sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 0.25 }}
                                    >
                                      Insurance Recommended
                                    </AlertTitle>
                                    <Typography variant='body2'>
                                      This shipment is going to a practice address. Insurance is
                                      recommended.
                                    </Typography>
                                  </Alert>
                                )}

                                {hasInsurance && (
                                  <Box sx={{ mt: 3 }}>
                                    <TextField
                                      fullWidth
                                      label='Insurance amount'
                                      disabled={isCreatingLabel}
                                      value={insuranceAmount}
                                      error={!!insuranceAmountError}
                                      helperText={insuranceAmountError}
                                      onChange={({ target: { value } }) => {
                                        if (/^\d*\.?\d*$/.test(value)) {
                                          setInsuranceAmount(value);
                                        }
                                      }}
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position='start'>
                                            <AttachMoneyIcon sx={{ color: 'text.secondary' }} />
                                          </InputAdornment>
                                        ),
                                        inputProps: { inputMode: 'decimal' },
                                      }}
                                    />
                                  </Box>
                                )}
                              </Grid>
                            </Grid>
                          </Box>
                        </Collapse>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              </Grid>
            )}

            {/* Right Panel - Manual Shipping Form */}
            {shippingService === 'Manual' && (
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    position: 'sticky',
                    top: 20,
                    boxShadow: theme.shadows[3],
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: 'background.paper',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      onClick={() => setIsManualFormExpanded(!isManualFormExpanded)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.8,
                        },
                      }}
                    >
                      <KeyboardArrowDownIcon
                        sx={{
                          color: 'primary.main',
                          transform: isManualFormExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                          transition: 'transform 0.3s ease',
                        }}
                      />
                      <Typography variant='h6' fontWeight={600} color='primary.main'>
                        Shipment Details
                      </Typography>
                    </Box>

                    <Collapse in={isManualFormExpanded}>
                      <Box sx={{ mt: 2 }}>
                        <Form
                          onSubmit={handleCreateManualShipment}
                          initialValues={{
                            carrierCode: 'UPS',
                          }}
                          render={({ handleSubmit, values }) => {
                            const isFormValid =
                              values?.trackingNumber && values?.shipDate && values?.shipTime;

                            return (
                              <form noValidate onSubmit={handleSubmit}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12}>
                                    <SelectField
                                      id='Shipment-Manually-dropdown'
                                      label='Carrier Code'
                                      name='carrierCode'
                                      value='UPS'
                                      disabled
                                    >
                                      <MenuItem value='UPS'>UPS</MenuItem>
                                    </SelectField>
                                  </Grid>

                                  <Grid item xs={12}>
                                    <FormTextField
                                      label='Tracking Number'
                                      name='trackingNumber'
                                      required
                                    />
                                  </Grid>

                                  <Grid item xs={12}>
                                    <DateField
                                      label='Shipping Date'
                                      name='shipDate'
                                      id='BatchShipmentsTable-shipDate-dateField'
                                      required
                                      maxDate={new Date()}
                                    />
                                  </Grid>

                                  <Grid item xs={12}>
                                    <TimeField
                                      label='Shipping Time (24-hour)'
                                      name='shipTime'
                                      id='BatchShipmentsTable-shipTime-timeField'
                                      required
                                      helperText='Use 24-hour format, e.g., 14:30'
                                      validations={[
                                        validateTimeNotInFuture(
                                          'Time cannot be in the future',
                                          'shipDate'
                                        ),
                                      ]}
                                    />
                                  </Grid>

                                  <Grid item xs={12}>
                                    <FormTextField
                                      label='External Shipment ID (If Available)'
                                      name='externalShipmentId'
                                    />
                                  </Grid>

                                  <Grid item xs={12}>
                                    <Box
                                      sx={{
                                        bgcolor: 'grey.100',
                                        p: 2,
                                        borderRadius: 1,
                                      }}
                                    >
                                      <Typography variant='body2' color='text.secondary'>
                                        I acknowledge that shipment {referenceId || shipmentId} has
                                        been verified. Shipment details have been manually entered
                                        and will not be processed via Shipment Service.
                                      </Typography>
                                    </Box>
                                  </Grid>

                                  <Grid item xs={12}>
                                    <Button
                                      type='submit'
                                      variant='contained'
                                      fullWidth
                                      disabled={isSendingToShipStation || !isFormValid}
                                      sx={{ textTransform: 'none', mt: 1 }}
                                    >
                                      {isSendingToShipStation ? 'Creating...' : 'Create Shipment'}
                                    </Button>
                                  </Grid>
                                </Grid>
                              </form>
                            );
                          }}
                        />
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>

        {!isShipped && shippingService === 'FedEx' && (
          <Box
            sx={{
              px: 20,
              pb: 2,
              bgcolor: 'transparent',
              flexShrink: 0,
              position: 'relative',
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            <Grid container spacing={2}>
              <Grid item md={8} sx={{ display: { xs: 'none', md: 'block' } }} />
              <Grid item xs={12} md={4}>
                {/* POM-5507: the Cost / Create Label bar. On error it grows
                    upward to reveal a non-blocking, dismissible message inline —
                    no modal, so the user can pick a supported option in place. */}
                <Box
                  sx={{
                    bgcolor: 'primary.light',
                    borderRadius: 2,
                    pointerEvents: 'auto',
                    boxShadow: theme.shadows[4],
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* POM-5507: the error folds into the same bar and slides up
                      from the bottom (Collapse height transition). */}
                  <Collapse in={Boolean(inlineLabelError)} timeout={300} unmountOnExit>
                    <Alert
                      severity='error'
                      icon={<ErrorIcon fontSize='inherit' />}
                      onClose={() => setInlineLabelError('')}
                      sx={{ borderRadius: 0, alignItems: 'flex-start' }}
                    >
                      {inlineLabelError}
                    </Alert>
                  </Collapse>
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box>
                        <Typography
                          variant='caption'
                          sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block' }}
                        >
                          Cost
                        </Typography>
                        <Typography
                          variant='h6'
                          fontWeight={700}
                          sx={{
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          $
                          {isUpdatingShipment ? (
                            <CircularProgress size={18} sx={{ color: 'white' }} />
                          ) : (
                            getDisplayCost()
                          )}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant='contained'
                      size='large'
                      startIcon={
                        <LocalShippingIcon
                          sx={{
                            animation: isCreatingLabel
                              ? 'truckLoading 1.5s ease-in-out infinite'
                              : 'none',
                            '@keyframes truckLoading': {
                              '0%, 100%': { transform: 'translateX(0)' },
                              '50%': { transform: 'translateX(10px)' },
                            },
                          }}
                        />
                      }
                      onClick={handleCreateLabel}
                      disabled={
                        allItemsMoved ||
                        isCreatingLabel ||
                        !isShipDateValid ||
                        !isValidWeight ||
                        (packageCode === 'YOUR_PACKAGING' && !dimensions) ||
                        !!insuranceAmountError
                      }
                      sx={{
                        py: 1.25,
                        px: 3,
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        bgcolor: 'white',
                        color: 'primary.main',
                        '&:hover': { bgcolor: 'grey.100' },
                        '&.Mui-disabled': {
                          bgcolor: 'rgba(255,255,255,0.4)',
                          color: 'rgba(0,0,0,0.3)',
                        },
                      }}
                    >
                      {isCreatingLabel ? labelProgress : 'Create Label'}
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        <EditShipmentAddressDialog
          open={isEditAddressDialogOpen}
          onClose={() => setIsEditAddressDialogOpen(false)}
          shipment={displayShipment}
          onAddressUpdated={applyAddressUpdate}
        />
      </Dialog>

      {/* Send to ShipStation confirmation dialog */}
      <Dialog
        open={isSendToShipstationDialogOpen}
        onClose={handleCloseSendToShipStationDialog}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Send to ShipStation</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>
            This will create a shipment in ShipStation for order {referenceId}. You&apos;ll need to
            manage the shipment externally.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size='small'>
                <InputLabel id='ShipStation-shipping-method-label'>
                  Select Shipping Method
                </InputLabel>
                <Select
                  labelId='ShipStation-shipping-method-label'
                  label='Select Shipping Method'
                  value={shipStationShipmentType}
                  onChange={(e) => setShipStationShipmentType(e.target.value)}
                >
                  {map(
                    sortBy(shippingOptions, ({ shippingOption }) =>
                      String(shippingOption ?? '').toLowerCase()
                    ),
                    ({ optionCode, shippingOption }) => (
                      <MenuItem key={optionCode} value={optionCode}>
                        {shippingOption}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSendToShipStationDialog} disabled={isSendingToShipStation}>
            Cancel
          </Button>
          <LoadingButton
            variant='contained'
            onClick={handleConfirmSendToShipStation}
            loading={isSendingToShipStation}
            disabled={isSendingToShipStation}
          >
            Send
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ManageShipmentItemsDialog
        open={manageItemsOpen}
        shipment={{
          ...displayShipment,
          shipmentAddress:
            selectedAddressType === 'validated' && validatedShipmentAddress
              ? validatedShipmentAddress
              : originalShipmentAddress || displayShipment.shipmentAddress,
        }}
        fulfillingPharmacyName={getFulfillingPharmacyName()}
        onClose={() => setManageItemsOpen(false)}
        onSuccess={(movedOutIds, addedItems) => {
          setDisplayShipment((prev) => {
            const filtered = movedOutIds?.size
              ? (prev.shipmentItems || []).filter((item) => !movedOutIds.has(item.lineItemId))
              : prev.shipmentItems || [];
            const incoming = (addedItems || []).map(
              ({ sourceShipmentId, sourceReferenceId, ...item }) => item
            );
            return { ...prev, shipmentItems: [...filtered, ...incoming] };
          });
          if (onAfterMutation) onAfterMutation();
        }}
        onAllItemsMoved={() => {
          setAllItemsMoved(true);
          setDisplayShipment((prev) => ({ ...prev, shipmentItems: [] }));
        }}
      />

      <RemoveShipmentItemsDialog
        open={removeItemsOpen}
        onClose={() => setRemoveItemsOpen(false)}
        shipment={{
          shipmentId: displayShipment?.shipmentId,
          referenceId: displayShipment?.referenceId,
          recipientName: displayShipment?.recipientName,
          partnerName: displayShipment?.partnerName,
          practiceName: displayShipment?.practiceName,
          fulfillingPharmacyName: getFulfillingPharmacyName(),
          totalItems: (displayShipment?.shipmentItems || []).length,
        }}
        shipmentItems={displayShipment?.shipmentItems || []}
        onSuccess={(removedIds) => {
          const removed = new Set(removedIds);
          setDisplayShipment((prev) => ({
            ...prev,
            shipmentItems: (prev.shipmentItems || []).filter((i) => !removed.has(i.lineItemId)),
          }));
          setRemoveItemsOpen(false);
          refreshAfterShipmentMutation();
        }}
      />

      {/* Cancel Shipment Confirmation Dialog */}
      <CancelShipmentDialog
        open={cancelShipmentDialogOpen}
        onClose={() => setCancelShipmentDialogOpen(false)}
        shipmentId={referenceId}
        onConfirm={handleCancelShipment}
        isLoading={isCancellingShipment}
        hideOrderCancelOption={hideOrderCancelOption}
      />

      {/* Void Label Confirmation Dialog */}
      <Dialog
        open={voidLabelDialogOpen}
        onClose={() => setVoidLabelDialogOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Void Label</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to void this label? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            color='secondary'
            onClick={() => setVoidLabelDialogOpen(false)}
            disabled={isVoidingLabel}
          >
            No
          </Button>
          <LoadingButton
            loading={isVoidingLabel}
            onClick={handleVoidLabel}
            color='error'
            variant='contained'
          >
            Yes
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ShippingTimelineModal
        open={timelineOpen}
        onClose={() => setTimelineOpen(false)}
        shipmentId={shipment?.shipmentId}
        referenceId={shipment?.referenceId}
      />
    </>
  );
};

ShipmentViewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAfterMutation: PropTypes.func,
  disableVoidLabel: PropTypes.bool,
  hideOrderCancelOption: PropTypes.bool,
  shipment: PropTypes.shape({
    shipmentId: PropTypes.string,
    referenceId: PropTypes.string,
    recipientName: PropTypes.string,
    shipmentPhoneNumber: PropTypes.string,
    shipmentAddress: PropTypes.shape({
      line1: PropTypes.string,
      line2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
      isVerified: PropTypes.bool,
      addressDirectoryId: PropTypes.string,
    }),
    partnerName: PropTypes.string,
    practiceName: PropTypes.string,
    shipmentStatus: PropTypes.string,
    serviceCode: PropTypes.string,
    packageCode: PropTypes.string,
    dimensions: PropTypes.shape({
      length: PropTypes.number,
      width: PropTypes.number,
      height: PropTypes.number,
    }),
    isBatchShipment: PropTypes.bool,
    carrierCode: PropTypes.string,
    fulfillingPharmacyId: PropTypes.string,
    cost: PropTypes.number,
    insuranceAmountInUSD: PropTypes.number,
    trackingNumber: PropTypes.string,
    shipmentCreatedAt: PropTypes.string,
    shipmentShippedAt: PropTypes.string,
    pickedUpTimestamp: PropTypes.string,
    outForDeliveryTimestamp: PropTypes.string,
    deliveredTimestamp: PropTypes.string,
    labelUrl: PropTypes.string,
    shipmentWeight: PropTypes.shape({
      value: PropTypes.number,
      units: PropTypes.string,
    }),
    shipDate: PropTypes.string,
    enableSaturdayDelivery: PropTypes.bool,
    signatureRequired: PropTypes.bool,
    clientMetadata: PropTypes.shape({
      shipDate: PropTypes.string,
    }),
    shipmentItems: PropTypes.arrayOf(
      PropTypes.shape({
        lineItemId: PropTypes.string,
        orderId: PropTypes.string,
        name: PropTypes.string,
        sku: PropTypes.string,
        quantity: PropTypes.string,
      })
    ),
    notes: PropTypes.arrayOf(
      PropTypes.shape({
        noteId: PropTypes.string.isRequired,
        noteContent: PropTypes.string,
        noteState: PropTypes.string,
        noteCreatedAt: PropTypes.string,
        noteAuthor: PropTypes.shape({
          firstName: PropTypes.string,
          lastName: PropTypes.string,
          pomUserId: PropTypes.string,
        }),
        noteContextType: PropTypes.string,
        noteContextId: PropTypes.string,
      })
    ),
    deliveryInstructions: PropTypes.string,
    originalShipmentAddress: PropTypes.shape({
      line1: PropTypes.string,
      line2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
    }),
    originalServiceCode: PropTypes.string,
    validatedShipmentAddress: PropTypes.shape({
      line1: PropTypes.string,
      line2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
      addressValidity: PropTypes.string,
      addressDeficiencies: PropTypes.arrayOf(PropTypes.string),
      addressDirectoryId: PropTypes.string,
    }),
    enableInsurance: PropTypes.bool,
  }),
};

ShipmentViewDialog.defaultProps = {
  shipment: null,
  onAfterMutation: undefined,
  disableVoidLabel: false,
  hideOrderCancelOption: false,
};

export default ShipmentViewDialog;
